import React, { JSXElementConstructor, lazy, useRef } from 'react';
import SetupComplete from '../../views/SetupComplete';
import * as entities from '../../graphql/entities';
import { SystemFolder, SystemScreen } from '../../graphql/entities';
import EcSalesListingForm from '../../views/vat/EcSalesListingForm';
import { ALLOW_ADD, ALLOW_DELETE, ALLOW_EDIT, ALLOW_VIEW } from '../../utils/OperationPermissions';
import LuckinsItem from '../../graphql/entities/product/LuckinsItem';
import LuckinsEnquiryView from '../../views/product/LuckinsEnquiryView';
import LuckinsItemForm from '../../views/product/LuckinsItemForm';
import CustomerStatementResultWorkspace from '../../views/customer/CustomerStatementResultWorkspace';
import DebitNoteDocument from '../../graphql/entities/purchases/DebitNoteDocument';
import CustomerContractReferenceWorkspace from '../../views/sales/CustomerContractReferenceWorkspace';
import CustomerContractReferenceForm from '../../views/sales/CustomerContractReferenceForm';
import UserNotificationWorkspace from '../../views/company/UserNotificationWorkspace';
import AutomaticDispatchResultWorkspace from '../../views/product/AutomaticDispatchResultWorkspace';
import {
  FilterOperation,
  IFilterInput,
  IFolder,
  IScreen,
  IScreenFolderMutationResult,
  IScreenFolderMutationVariables,
  ISystemFolderAndScreenMutationResult,
  ISystemFolderAndScreenMutationVariables,
  Maybe,
  StockTakeType,
} from '../../graphql/schema/p4types';
import getIn from '../connectedForm/utils/getIn';
import setIn from '../connectedForm/utils/setIn';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { menuActions } from '../../state/modules/menu';
import CreditControl from '../../graphql/entities/customer/CreditControl';
import { P4SVGIcon } from '../SVGIcons/SVGIcons';
import ProductsStockLotWorkspace from '../../views/product/ProductsStockLotWorkspace';
import StockTake from '../../graphql/entities/product/StockTake';
import NominalWarehouseAccountSet from '../../graphql/entities/nominal/NominalWarehouseAccountSet';
import SalesOrderDeliveryAddress from '../../graphql/entities/sales/SalesOrderDeliveryAddress';
import EmailPrintPreview from '../../graphql/entities/company/EmailPrintPreview';
import { RouteComponentProps } from 'react-router';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { IAppMenu, IMenuEntry, IMenuSection, isMenuEntry, ISubMenuSection } from '../../@types/menuTypes';
import AssemblyReservationTreeSpaceWithContext from '../../views/product/AssemblyReservationTreeSpaceWithContext';
import EdataEnquiryView from '../../views/product/EdataEnquiryView';
import EdataProductForm from '../../views/product/EdataProductDetails';
import EdataProduct from '../../graphql/entities/product/EdataProduct';
import EdataConfigForm from '../../views/product/EdataConfigForm';

const AuthorityWorkspace = lazy(
  () => import(/* webpackChunkName: "AuthorityWorkspace" */ '../../views/vat/VatAuthorityWorkspace')
);
const AuthorityForm = lazy(() => import(/* webpackChunkName: "AuthorityForm" */ '../../views/vat/VatAuthorityForm'));
const RateWorkspace = lazy(() => import(/* webpackChunkName: "RateWorkspace" */ '../../views/vat/VatRateWorkspace'));
const RateForm = lazy(() => import(/* webpackChunkName: "RateForm" */ '../../views/vat/VatRateForm'));
const VatBusinessCategoryWorkspace = lazy(
  () => import(/* webpackChunkName: "VatBusinessCategoryWorkspace" */ '../../views/vat/VatBusinessCategoryWorkspace')
);
const ProductCategoryAttributeConfigForm = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductCategoryAttributeConfigForm" */ '../../views/product/ProductCategoryAttributeConfigForm'
    )
);
const VatBusinessCategoryForm = lazy(
  () => import(/* webpackChunkName: "VatBusinessCategoryForm" */ '../../views/vat/VatBusinessCategoryForm')
);
const VatProductCategoryWorkspace = lazy(
  () => import(/* webpackChunkName: "VatProductCategoryWorkspace" */ '../../views/vat/VatProductCategoryWorkspace')
);
const VatProductCategoryForm = lazy(
  () => import(/* webpackChunkName: "VatProductCategoryForm" */ '../../views/vat/VatProductCategoryForm')
);
const VatTransactionWorkspace = lazy(
  () => import(/* webpackChunkName: "VatTransactionWorkspace" */ '../../views/vat/VatTransactionWorkspace')
);
const ReconciliationForm = lazy(
  () => import(/* webpackChunkName: "ReconciliationForm" */ '../../views/vat/VatReconciliationForm')
);
const ReturnWorkspace = lazy(
  () => import(/* webpackChunkName: "ReturnWorkspace" */ '../../views/vat/VatReturnWorkspace')
);
const ReturnForm = lazy(() => import(/* webpackChunkName: "ReturnForm" */ '../../views/vat/VatReturnForm'));
const RateTotalsForm = lazy(() => import(/* webpackChunkName: "RateTotalsForm" */ '../../views/vat/VatRateTotalsForm'));
const SwitchForm = lazy(() => import(/* webpackChunkName: "SwitchForm" */ '../../views/vat/VatSwitchForm'));

const BankForm = lazy(() => import(/* webpackChunkName: "BankForm" */ '../../views/bank/BankForm'));
const BankFeedForm = lazy(
  () => import(/* webpackChunkName: "BankFeedForm" */ '../../views/bank/bankFeed/BankFeedForm')
);
const BankWorkspace = lazy(() => import(/* webpackChunkName: "BankWorkspace" */ '../../views/bank/BankWorkspace'));
const BankFeedWorkspace = lazy(
  () => import(/* webpackChunkName: "BankFeedWorkspace" */ '../../views/bank/BankFeedWorkspace')
);
const BankPaymentReceiptForm = lazy(
  () => import(/* webpackChunkName: "BankPaymentReceiptForm" */ '../../views/bank/BankPaymentReceiptForm')
);
const BankPaymentReceiptWorkspace = lazy(
  () => import(/* webpackChunkName: "BankPaymentReceiptWorkspace" */ '../../views/bank/BankPaymentReceiptWorkspace')
);
const BankReconciliationForm = lazy(
  () => import(/* webpackChunkName: "BankReconciliationForm" */ '../../views/bank/BankReconciliationForm')
);
const BankReconciliationWorkspace = lazy(
  () => import(/* webpackChunkName: "BankReconciliationWorkspace" */ '../../views/bank/BankReconciliationWorkspace')
);
const BankConfigForm = lazy(() => import(/* webpackChunkName: "BankConfigForm" */ '../../views/bank/BankConfigForm'));
const BankTransferForm = lazy(
  () => import(/* webpackChunkName: "BankTransferForm" */ '../../views/bank/BankTransferForm')
);
const BankTransferWorkspace = lazy(
  () => import(/* webpackChunkName: "BankTransferWorkspace" */ '../../views/bank/BankTransferWorkspace')
);
const BankRevaluationForm = lazy(
  () => import(/* webpackChunkName: "BankRevaluationForm" */ '../../views/bank/BankRevaluationForm')
);
const BankRevaluationWorkspace = lazy(
  () => import(/* webpackChunkName: "BankRevaluationWorkspace" */ '../../views/bank/BankRevaluationWorkspace')
);

const AttributeWorkspace = lazy(
  () => import(/* webpackChunkName: "AttributeWorkspace" */ '../../views/diary/DiaryAttributeConfigWorkspace')
);
const AttributeForm = lazy(
  () => import(/* webpackChunkName: "AttributeForm" */ '../../views/diary/DiaryAttributeConfigForm')
);
const EventWorkspace = lazy(
  () => import(/* webpackChunkName: "EventWorkspace" */ '../../views/diary/DiaryEventWorkspace')
);
const EventForm = lazy(() => import(/* webpackChunkName: "EventForm" */ '../../views/diary/DiaryEventForm'));
const ResultWorkspace = lazy(
  () => import(/* webpackChunkName: "ResultWorkspace" */ '../../views/diary/DiaryResultWorkspace')
);
const ResultForm = lazy(() => import(/* webpackChunkName: "ResultForm" */ '../../views/diary/DiaryResultForm'));
const TypeWorkspace = lazy(
  () => import(/* webpackChunkName: "TypeWorkspace" */ '../../views/diary/DiaryTypeWorkspace')
);
const TypeForm = lazy(() => import(/* webpackChunkName: "TypeForm" */ '../../views/diary/DiaryTypeForm'));
const DiaryGlobalAmendForm = lazy(
  () => import(/* webpackChunkName: "DiaryGlobalAmendForm" */ '../../views/diary/DiaryGlobalAmendForm')
);
const DiaryGlobalAmendResultsWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "DiaryGlobalAmendResultsWorkspace" */ '../../views/diary/DiaryGlobalAmendResultsWorkspace'
    )
);

const AcknowledgementWorkspace = lazy(
  () => import(/* webpackChunkName: "AcknowledgementWorkspace" */ '../../views/sales/AcknowledgementWorkspace')
);
const AcknowledgementPrintForm = lazy(
  () => import(/* webpackChunkName: "AcknowledgementPrintForm" */ '../../views/sales/AcknowledgementPrintForm')
);
const SalesAttributeConfigWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "SalesAttributeConfigWorkspace" */ '../../views/sales/SalesAttributeConfigWorkspace')
);
const SalesAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "SalesAttributeConfigForm" */ '../../views/sales/SalesAttributeConfigForm')
);
const SalesNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "SalesNoteTypeWorkspace" */ '../../views/sales/SalesNoteTypeWorkspace')
);
const SalesNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "SalesNoteTypeForm" */ '../../views/sales/SalesNoteTypeForm')
);
const OrderConfigForm = lazy(
  () => import(/* webpackChunkName: "OrderConfigForm" */ '../../views/sales/SalesOrderConfigForm')
);
const SalesOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "SalesOrderWorkspace" */ '../../views/sales/SalesOrderWorkspace')
);
const SalesOrderDeliveryAddressWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "SalesOrderDeliveryAddress" */ '../../views/sales/SalesOrderDeliveryAddressWorkspace')
);
const QuotationWorkspace = lazy(
  () => import(/* webpackChunkName: "QuotationWorkspace" */ '../../views/sales/QuotationWorkspace')
);
const CombinedSalesWorkspace = lazy(
  () => import(/* webpackChunkName: "CombinedSalesWorkspace" */ '../../views/sales/CombinedSalesWorkspace')
);
const SalesOrderForm = lazy(
  () => import(/* webpackChunkName: "SalesOrderForm" */ '../../views/sales/salesOrderForm/SalesOrderForm')
);
const QuotationForm = lazy(() => import(/* webpackChunkName: "QuotationForm" */ '../../views/sales/QuotationForm'));
const SalesOrderAuditForm = lazy(
  () => import(/* webpackChunkName: "SalesOrderAuditForm" */ '../../views/sales/SalesOrderAuditForm')
);
const SalesOrderLineAuditForm = lazy(
  () => import(/* webpackChunkName: "SalesOrderLineAuditForm" */ '../../views/sales/SalesOrderLineAuditForm')
);
const DeliveryMethods = lazy(
  () => import(/* webpackChunkName: "DeliveryMethods" */ '../../views/sales/DeliveryMethodsWorkspace')
);
const DeliveryRoutes = lazy(
  () => import(/* webpackChunkName: "DeliveryRoutes" */ '../../views/sales/DeliveryRouteWorkspace')
);
const Carriers = lazy(() => import(/* webpackChunkName: "Carriers" */ '../../views/sales/CarrierWorkspace'));
const PacTerminals = lazy(
  () => import(/* webpackChunkName: "PacTerminals" */ '../../views/sales/PacTerminalWorkspace')
);
const CarrierForm = lazy(() => import(/* webpackChunkName: "CarrierForm" */ '../../views/sales/CarrierForm'));
const PacTerminalForm = lazy(() => import(/* webpackChunkName: "CarrierForm" */ '../../views/sales/PacTerminalForm'));
const DeliveryMethodsForm = lazy(
  () => import(/* webpackChunkName: "DeliveryMethodsForm" */ '../../views/sales/DeliveryMethodsForm')
);
const DeliveryRouteForm = lazy(
  () => import(/* webpackChunkName: "DeliveryRoutesForm" */ '../../views/sales/DeliveryRouteForm')
);
const SalesDocumentConfigForm = lazy(
  () => import(/* webpackChunkName: "SalesDocumentConfigForm" */ '../../views/sales/SalesDocumentConfigForm')
);
const ProductDocumentConfigForm = lazy(
  () => import(/* webpackChunkName: "ProductDocumentConfigForm" */ '../../views/product/ProductDocumentConfigForm')
);
const SalesOrderLineWorkspace = lazy(
  () => import(/* webpackChunkName: "SalesOrderLineWorkspace" */ '../../views/sales/SalesOrderLineWorkspace')
);
const PricingConfigForm = lazy(
  () => import(/* webpackChunkName: "PricingConfigForm" */ '../../views/sales/PricingConfigForm')
);
const PaymentMethod = lazy(
  () => import(/* webpackChunkName: "PaymentMethod" */ '../../views/sales/PaymentMethodWorkspace')
);
const PaymentMethodForm = lazy(
  () => import(/* webpackChunkName: "PaymentMethodForm" */ '../../views/sales/PaymentMethodForm')
);
const DispatchInstructionsWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "DispatchInstructionsWorkspace" */ '../../views/sales/DispatchInstructionsWorkspace')
);
const DispatchInstructionLinesWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "DispatchInstructionLinesWorkspace" */ '../../views/sales/DispatchInstructionLineWorkspace'
    )
);
const IntrastatDispatchInstructionLinesWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "IntrastatDispatchInstructionLinesWorkspace" */ '../../views/sales/IntrastatDispatchInstructionLinesWorkspace'
    )
);
const IntrastatGoodsReceiptLinesWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "IntrastatGoodsReceiptLinesWorkspace" */ '../../views/purchases/IntrastatGoodsReceiptLineWorkspace'
    )
);
const ManualDispatchForm = lazy(
  () => import(/* webpackChunkName: "ManualDispatchForm" */ '../../views/sales/manualDispatchForm/ManualDispatchForm')
);
const PickingInstructionPrintForm = lazy(
  () => import(/* webpackChunkName: "PickingInstructionPrintForm" */ '../../views/sales/PickingInstructionPrintForm')
);
const PickingBoxForm = lazy(() => import(/* webpackChunkName: "PickingBoxForm" */ '../../views/sales/PickingBoxForm'));
const ToteForm = lazy(() => import(/* webpackChunkName: "ToteForm" */ '../../views/sales/ToteForm'));
const StaticBoxForm = lazy(() => import(/* webpackChunkName: "StaticBoxForm" */ '../../views/web/StaticBoxForm'));
const InvoiceGenerationCriteriaResult = lazy(
  () =>
    import(
      /* webpackChunkName: "InvoiceGenerationCriteriaResult" */ '../../views/sales/InvoiceGenerationCriteriaResult'
    )
);
const EbayConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "EbayConfigWorkspace" */ '../../views/sales/EbayConfigWorkspace')
);
const EbayConfigForm = lazy(() => import(/* webpackChunkName: "EbayConfigForm" */ '../../views/sales/EbayConfigForm'));
const AmazonConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "AmazonConfigWorkspace" */ '../../views/sales/AmazonConfigWorkspace')
);
const AmazonSettlementReportItemWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "AmazonSettlementReportItemWorkspace" */ '../../views/sales/AmazonSettlementReportItemWorkspace'
    )
);
const AmazonConfigForm = lazy(
  () => import(/* webpackChunkName: "AmazonConfigForm" */ '../../views/sales/AmazonConfigForm')
);
const ShopifyConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "ShopifyConfigWorkspace" */ '../../views/sales/ShopifyConfigWorkspace')
);
const ShopifyConfigForm = lazy(
  () => import(/* webpackChunkName: "ShopifyConfigForm" */ '../../views/sales/ShopifyConfigForm')
);
const PixSellConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "PixSellConfigWorkspace" */ '../../views/sales/PixSellConfigWorkspace')
);
const PixSellConfigForm = lazy(
  () => import(/* webpackChunkName: "PixSellConfigForm" */ '../../views/sales/PixSellConfigForm')
);
const WooCommerceConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "WooCommerceConfigWorkspace" */ '../../views/sales/WooCommerceConfigWorkspace')
);
const WooCommerceConfigForm = lazy(
  () => import(/* webpackChunkName: "WooCommerceConfigForm" */ '../../views/sales/WooCommerceConfigForm')
);
const EdiConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "EdiConfigWorkspace" */ '../../views/sales/EdiConfigWorkspace')
);
const EdiConfigForm = lazy(() => import(/* webpackChunkName: "EdiConfigForm" */ '../../views/sales/EdiConfigForm'));
const EtsyConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "EtsyConfigWorkspace" */ '../../views/sales/EtsyConfigWorkspace')
);
const EtsyConfigForm = lazy(() => import(/* webpackChunkName: "EtsyConfigForm" */ '../../views/sales/EtsyConfigForm'));
const BigCommerceConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "BigCommerceConfigWorkspace" */ '../../views/sales/BigCommerceConfigWorkspace')
);
const BigCommerceConfigForm = lazy(
  () => import(/* webpackChunkName: "BigCommerceConfigForm" */ '../../views/sales/BigCommerceConfigForm')
);
const MagentoConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "MagentoConfigWorkspace" */ '../../views/sales/MagentoConfigWorkspace')
);
const MagentoConfigForm = lazy(
  () => import(/* webpackChunkName: "MagentoConfigForm" */ '../../views/sales/MagentoConfigForm')
);
const WebConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "WebConfigWorkspace" */ '../../views/sales/WebConfigWorkspace')
);
const WebConfigForm = lazy(() => import(/* webpackChunkName: "WebConfigForm" */ '../../views/sales/WebConfigForm'));
const ChannelConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "ChannelConfigWorkspace" */ '../../views/sales/ChannelConfigWorkspace')
);
const ChannelConfigForm = lazy(
  () => import(/* webpackChunkName: "ChannelConfigForm" */ '../../views/sales/ChannelConfigForm')
);
const WebPageForm = lazy(() => import(/* webpackChunkName: "WebPageForm" */ '../../views/web/WebPageForm'));
const WebMediaFolderForm = lazy(
  () => import(/* webpackChunkName: "WebMediaFolderForm" */ '../../views/web/WebMediaFolderForm')
);

const WebPromotionalCodeForm = lazy(
  () => import(/* webpackChunkName: "WebPromotionalCodeForm" */ '../../views/web/WebPromotionalCodeForm')
);

const WebPageWorkspace = lazy(
  () => import(/* webpackChunkName: "WebPageWorkspace" */ '../../views/web/WebPageWorkspace')
);

const WebPromotionalCodeWorkspace = lazy(
  () => import(/* webpackChunkName: "WebPromotionalCodeWorkspace" */ '../../views/web/WebPromotionalCodeWorkspace')
);

const QuotationDocumentPrintForm = lazy(
  () => import(/* webpackChunkName: "QuotationDocumentPrintForm" */ '../../views/sales/QuotationDocumentPrintForm')
);
const ImportedAmazonOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportedAmazonOrderWorkspace" */ '../../views/sales/ImportedAmazonOrderWorkspace')
);
const ImportedAmazonOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedAmazonOrderForm" */ '../../views/sales/ImportedAmazonOrderForm')
);
const ImportedEbayOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportedEbayOrderWorkspace" */ '../../views/sales/ImportedEbayOrderWorkspace')
);
const ImportedEbayOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedEbayOrderForm" */ '../../views/sales/ImportedEbayOrderForm')
);
const ImportedShopifyOrderWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "ImportedShopifyOrderWorkspace" */ '../../views/sales/ImportedShopifyOrderWorkspace')
);
const ImportedShopifyOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedShopifyOrderForm" */ '../../views/sales/ImportedShopifyOrderForm')
);
const ImportedPixSellOrderWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "ImportedPixSellOrderWorkspace" */ '../../views/sales/ImportedPixSellOrderWorkspace')
);
const ImportedPixSellOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedPixSellOrderForm" */ '../../views/sales/ImportedPixSellOrderForm')
);
const ImportedWooCommerceOrderWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ImportedWooCommerceOrderWorkspace" */ '../../views/sales/ImportedWooCommerceOrderWorkspace'
    )
);
const ImportedWooCommerceOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedWooCommerceOrderForm" */ '../../views/sales/ImportedWooCommerceOrderForm')
);
const ImportedXediOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportedXediOrderWorkspace" */ '../../views/sales/ImportedXediOrderWorkspace')
);
const ImportedXediOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedXediOrderForm" */ '../../views/sales/ImportedXediOrderForm')
);
const ImportedEtsyOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportedEtsyOrderWorkspace" */ '../../views/sales/ImportedEtsyOrderWorkspace')
);
const ImportedEtsyOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedEtsyOrderForm" */ '../../views/sales/ImportedEtsyOrderForm')
);
const ImportedBigCommerceOrderWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ImportedBigCommerceOrderWorkspace" */ '../../views/sales/ImportedBigCommerceOrderWorkspace'
    )
);
const ImportedBigCommerceOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedBigCommerceOrderForm" */ '../../views/sales/ImportedBigCommerceOrderForm')
);
const ImportedMagentoOrderWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "ImportedMagentoOrderWorkspace" */ '../../views/sales/ImportedMagentoOrderWorkspace')
);
const ImportedMagentoOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedMagentoOrderForm" */ '../../views/sales/ImportedMagentoOrderForm')
);
const ImportedWebOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportedWebOrderWorkspace" */ '../../views/sales/ImportedWebOrderWorkspace')
);
const ImportedWebOrderForm = lazy(
  () => import(/* webpackChunkName: "ImportedWebOrderForm" */ '../../views/sales/ImportedWebOrderForm')
);
const TillExperiment = lazy(() => import(/* webpackChunkName: "TillExperiment" */ '../../views/sales/TillExperiment'));
const ProformaInvoiceForm = lazy(
  () => import(/* webpackChunkName: "ProformaInvoiceForm" */ '../../views/sales/ProformaInvoiceForm')
);
const DataSheetForm = lazy(() => import(/* webpackChunkName: "DataSheetForm" */ '../../views/sales/DataSheetForm'));
const TradeCounterTerminalWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "TradeCounterTerminalWorkspace" */ '../../views/sales/TradeCounterTerminalWorkspace')
);
const TradeCounterTerminalForm = lazy(
  () => import(/* webpackChunkName: "TradeCounterTerminalForm" */ '../../views/sales/TradeCounterTerminalForm')
);
const TradeCounterReconcileWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "TradeCounterReconcileWorkspace" */ '../../views/sales/TradeCounterReconcileWorkspace')
);
const TradeCounterReconcileForm = lazy(
  () => import(/* webpackChunkName: "TradeCounterReconcileForm" */ '../../views/sales/TradeCounterReconcileForm')
);
const PaymentLineWorkspace = lazy(
  () => import(/* webpackChunkName: "PaymentLineWorkspace" */ '../../views/sales/PaymentLineWorkspace')
);
const ConnectTransactionWorkspace = lazy(
  () => import(/* webpackChunkName: "ConnectTransactionWorkspace" */ '../../views/sales/ConnectTransactionWorkspace')
);
const PickingBoxWorkspace = lazy(
  () => import(/* webpackChunkName: "PickingBoxWorkspace" */ '../../views/sales/PickingBoxWorkspace')
);
const PickingBoxLineWorkspace = lazy(
  () => import(/* webpackChunkName: "PickingBoxLineWorkspace" */ '../../views/sales/PickingBoxLineWorkspace')
);
const ToteWorkspace = lazy(() => import(/* webpackChunkName: "ToteWorkspace" */ '../../views/sales/ToteWorkspace'));
const CarriageCostsWorkspace = lazy(
  () => import(/* webpackChunkName: "CarriageCostsWorkspace" */ '../../views/sales/CarriageCostsWorkspace')
);
const MaxoptraConfigurationForm = lazy(
  () => import(/* webpackChunkName: "MaxoptraConfigurationForm" */ '../../views/sales/MaxoptraConfigurationForm')
);
const StaticBoxWorkspace = lazy(
  () => import(/* webpackChunkName: "StaticWorkspace" */ '../../views/web/StaticBoxWorkspace')
);
const CompanyWorkspace = lazy(
  () => import(/* webpackChunkName: "CompanyWorkspace" */ '../../views/system/SystemCompanyWorkspace')
);
const SystemCompanyForm = lazy(
  () => import(/* webpackChunkName: "SystemCompanyForm" */ '../../views/system/SystemCompanyForm')
);
const SmtpConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "SmtpConfigWorkspace" */ '../../views/system/SystemSmtpConfigWorkspace')
);
const SmtpConfigForm = lazy(
  () => import(/* webpackChunkName: "SmtpConfigForm" */ '../../views/system/SystemSmtpConfigForm')
);
const SmtpLogWorkspace = lazy(
  () => import(/* webpackChunkName: "SmtpLogWorkspace" */ '../../views/system/SystemSmtpLogWorkspace')
);
const SmtpLogForm = lazy(() => import(/* webpackChunkName: "SmtpLogForm" */ '../../views/system/SystemSmtpLogForm'));
const SystemPrinterWorkspace = lazy(
  () => import(/* webpackChunkName: "SystemPrinterWorkspace" */ '../../views/system/SystemSystemPrinterWorkspace')
);
const SystemPrinterForm = lazy(
  () => import(/* webpackChunkName: "SystemPrinterForm" */ '../../views/system/SystemSystemPrinterForm')
);
const SystemUserWorkspace = lazy(
  () => import(/* webpackChunkName: "SystemUserWorkspace" */ '../../views/system/SystemUserWorkspace')
);
const SystemUserForm = lazy(
  () => import(/* webpackChunkName: "SystemUserForm" */ '../../views/system/SystemSystemUserForm')
);
const LoggedInUserWorkspace = lazy(
  () => import(/* webpackChunkName: "LoggedInUserWorkspace" */ '../../views/system/LoggedInUserWorkspace')
);
const JobsWorkspace = lazy(() => import(/* webpackChunkName: "JobsWorkspace" */ '../../views/system/JobsWorkspace'));
const SystemConfigForm = lazy(
  () => import(/* webpackChunkName: "SystemConfigForm" */ '../../views/system/SystemConfigForm')
);
const RegisteredDevicesWorkspace = lazy(
  () => import(/* webpackChunkName: "RegisteredDevicesWorkspace" */ '../../views/system/RegisteredDevicesWorkspace')
);
const RegisteredDeviceForm = lazy(
  () => import(/* webpackChunkName: "RegisteredDeviceForm" */ '../../views/system/RegisteredDeviceForm')
);
const SystemLicenseForm = lazy(
  () => import(/* webpackChunkName: "SystemLicenseForm" */ '../../views/system/SystemLicenseForm')
);
const UserForm = lazy(() => import(/* webpackChunkName: "UserForm" */ '../../views/company/CompanyUserForm'));
const CompanyForm = lazy(() => import(/* webpackChunkName: "CompanyForm" */ '../../views/company/CompanyForm'));
const FinancialCalendarWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "FinancialCalendarWorkspace" */ '../../views/company/CompanyFinancialCalendarWorkspace')
);
const FinancialCalendarForm = lazy(
  () => import(/* webpackChunkName: "FinancialCalendarForm" */ '../../views/company/CompanyFinancialCalendarForm')
);
const ReportTemplateWorkspace = lazy(
  () => import(/* webpackChunkName: "ReportTemplateWorkspace" */ '../../views/company/CompanyReportTemplateWorkspace')
);
const ReportTemplateForm = lazy(
  () => import(/* webpackChunkName: "ReportTemplateForm" */ '../../views/company/CompanyReportTemplateForm')
);
const ReportTemplateFieldForm = lazy(
  () => import(/* webpackChunkName: "ReportTemplateFieldForm" */ '../../views/company/CompanyReportTemplateFieldForm')
);
const CompanyCompanyUserWorkspace = lazy(
  () => import(/* webpackChunkName: "CompanyCompanyUserWorkspace" */ '../../views/company/CompanyUserWorkspace')
);
const CompanyUserRolesWorkspace = lazy(
  () => import(/* webpackChunkName: "CompanyUserRolesWorkspace" */ '../../views/company/CompanyUserRoleWorkspace')
);
const CompanyUserRoleForm = lazy(
  () => import(/* webpackChunkName: "CompanyUserRoleForm" */ '../../views/company/CompanyUserRoleForm')
);
const ImportWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportWorkspace" */ '../../views/company/CommonImportWorkspace')
);
const ImportForm = lazy(() => import(/* webpackChunkName: "ImportForm" */ '../../views/company/CommonImportForm'));
const ImportTemplateForm = lazy(
  () => import(/* webpackChunkName: "ImportTemplateForm" */ '../../views/company/CommonImportTemplateForm')
);
const ImportTemplateWorkspace = lazy(
  () => import(/* webpackChunkName: "ImportTemplateWorkspace" */ '../../views/company/CommonImportTemplateWorkspace')
);
const CountryWorkspace = lazy(
  () => import(/* webpackChunkName: "CountryWorkspace" */ '../../views/company/CountryWorkspace')
);
const CountryForm = lazy(() => import(/* webpackChunkName: "CountryForm" */ '../../views/company/CountryForm'));
const CurrencyWorkspace = lazy(
  () => import(/* webpackChunkName: "CurrencyWorkspace" */ '../../views/company/CurrencyWorkspace')
);
const CurrencyForm = lazy(() => import(/* webpackChunkName: "CurrencyForm" */ '../../views/company/CurrencyForm'));
const OptionalMandatoryFieldsWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "OptionalMandatoryFieldsWorkspace" */ '../../views/company/OptionalMandatoryFieldsWorkspace'
    )
);
const OptionalMandatoryFieldsForm = lazy(
  () => import(/* webpackChunkName: "OptionalMandatoryFieldsForm" */ '../../views/company/OptionalMandatoryFieldsForm')
);
const StockValuationForm = lazy(
  () => import(/* webpackChunkName: "StockValuationForm" */ '../../views/company/StockValuationReportForm')
);
const FinancialAuditWorkspace = lazy(
  () => import(/* webpackChunkName: "FinancialAuditWorkspace" */ '../../views/company/FinancialAuditWorkspace')
);
const ManualScanAttachForm = lazy(
  () => import(/* webpackChunkName: "ManualScanAttachForm" */ '../../views/company/ManualScanAttachForm')
);
const DocumentSummary = lazy(
  () => import(/* webpackChunkName: "DocumentSummary" */ '../../views/company/DocumentSummaryWorkspace')
);
const CompanyReportTemplateFieldForm = lazy(
  () =>
    import(
      /* webpackChunkName: "CompanyReportTemplateFieldForm" */ '../../views/company/CompanyReportTemplateFieldForm'
    )
);
const LastOrderNumberForm = lazy(
  () => import(/* webpackChunkName: "LastOrderNumberForm" */ '../../views/company/LastOrderNumberForm')
);
const DocumentDesignForm = lazy(
  () => import(/* webpackChunkName: "DocumentDesignForm" */ '../../views/company/DocumentDesignForm')
);
const BulkAttachmentImportForm = lazy(
  () => import(/* webpackChunkName: "BulkAttachmentImportForm" */ '../../views/company/BulkAttachmentImportForm')
);
const EmailPrintPreviewForm = lazy(
  () => import(/* webpackChunkName: "EmailPrintPreviewForm" */ '../../views/company/EmailPrintPreviewForm')
);

const NominalBusinessCategoryWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "NominalBusinessCategoryWorkspace" */ '../../views/nominal/NominalBusinessCategoryWorkspace'
    )
);
const NominalBusinessCategoryForm = lazy(
  () => import(/* webpackChunkName: "NominalBusinessCategoryForm" */ '../../views/nominal/NominalBusinessCategoryForm')
);
const CommonAccountSetWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "CommonAccountSetWorkspace" */ '../../views/nominal/NominalCommonAccountSetWorkspace')
);
const CommonAccountSetForm = lazy(
  () => import(/* webpackChunkName: "CommonAccountSetForm" */ '../../views/nominal/NominalCommonAccountSetForm')
);
const NominalConfigForm = lazy(
  () => import(/* webpackChunkName: "NominalConfigForm" */ '../../views/nominal/NominalConfigForm')
);
const CustomerAccountSetWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerAccountSetWorkspace" */ '../../views/nominal/NominalCustomerAccountSetWorkspace'
    )
);
const CustomerAccountSetForm = lazy(
  () => import(/* webpackChunkName: "CustomerAccountSetForm" */ '../../views/nominal/NominalCustomerAccountSetForm')
);
const DimensionWorkspace = lazy(
  () => import(/* webpackChunkName: "DimensionWorkspace" */ '../../views/nominal/NominalDimensionWorkspace')
);
const DimensionForm = lazy(
  () => import(/* webpackChunkName: "DimensionForm" */ '../../views/nominal/NominalDimensionForm')
);
const NominalWorkspace = lazy(
  () => import(/* webpackChunkName: "NominalWorkspace" */ '../../views/nominal/NominalWorkspace')
);
const NominalForm = lazy(() => import(/* webpackChunkName: "NominalForm" */ '../../views/nominal/NominalForm'));
const NominalAuditForm = lazy(
  () => import(/* webpackChunkName: "NominalAuditForm" */ '../../views/nominal/NominalAuditForm')
);
const NominalNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "NominalNoteTypeWorkspace" */ '../../views/nominal/NominalNoteTypeWorkspace')
);
const NominalNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "NominalNoteTypeForm" */ '../../views/nominal/NominalNoteTypeForm')
);
const ProductAccountSetWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "ProductAccountSetWorkspace" */ '../../views/nominal/NominalProductAccountSetWorkspace')
);
const ProductAccountSetForm = lazy(
  () => import(/* webpackChunkName: "ProductAccountSetForm" */ '../../views/nominal/NominalProductAccountSetForm')
);
const NominalWarehouseAccountSetWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "NominalWarehouseAccountSetWorkspace" */ '../../views/nominal/NominalWarehouseAccountSetLineWorkspace'
    )
);
const NominalWarehouseAccountSetForm = lazy(
  () =>
    import(
      /* webpackChunkName: "NominalWarehouseAccountSetForm" */ '../../views/nominal/NominalWarehouseAccountSetForm'
    )
);
const NominalProductCategoryWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "NominalProductCategoryWorkspace" */ '../../views/nominal/NominalProductCategoryWorkspace'
    )
);
const NominalProductCategoryForm = lazy(
  () => import(/* webpackChunkName: "NominalProductCategoryForm" */ '../../views/nominal/NominalProductCategoryForm')
);
const ReportCategoryWorkspace = lazy(
  () => import(/* webpackChunkName: "ReportCategoryWorkspace" */ '../../views/nominal/NominalReportCategoryWorkspace')
);
const ReportCategoryForm = lazy(
  () => import(/* webpackChunkName: "ReportCategoryForm" */ '../../views/nominal/NominalReportCategoryForm')
);
const SupplierAccountSetWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "SupplierAccountSetWorkspace" */ '../../views/nominal/NominalSupplierAccountSetWorkspace'
    )
);
const SupplierAccountSetForm = lazy(
  () => import(/* webpackChunkName: "SupplierAccountSetForm" */ '../../views/nominal/NominalSupplierAccountSetForm')
);
const NominalTransactionWorkspace = lazy(
  () => import(/* webpackChunkName: "NominalTransactionWorkspace" */ '../../views/nominal/NominalTransactionWorkspace')
);
const NominalBudgetProfileWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "NominalBudgetProfileWorkspace" */ '../../views/nominal/NominalBudgetProfileWorkspace')
);
const NominalBudgetProfileForm = lazy(
  () => import(/* webpackChunkName: "NominalBudgetProfileForm" */ '../../views/nominal/NominalBudgetProfileForm')
);
const JournalWorkspace = lazy(
  () => import(/* webpackChunkName: "JournalWorkspace" */ '../../views/nominal/NominalJournalWorkspace')
);
const JournalForm = lazy(() => import(/* webpackChunkName: "JournalForm" */ '../../views/nominal/NominalJournalForm'));
const TemplateWorkspace = lazy(
  () => import(/* webpackChunkName: "TemplateWorkspace" */ '../../views/nominal/NominalTemplateWorkspace')
);
const TemplateForm = lazy(
  () => import(/* webpackChunkName: "TemplateForm" */ '../../views/nominal/NominalTemplateForm')
);
const AccrualPrepaymentWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "AccrualPrepaymentWorkspace" */ '../../views/nominal/NominalAccrualPrepaymentWorkspace')
);
const AccrualPrepaymentForm = lazy(
  () => import(/* webpackChunkName: "AccrualPrepaymentForm" */ '../../views/nominal/NominalAccrualPrepaymentForm')
);
const NominalAnalysis = lazy(
  () => import(/* webpackChunkName: "NominalAnalysis" */ '../../views/nominal/NominalAnalysisForm')
);
const ReportWorkspace = lazy(
  () => import(/* webpackChunkName: "ReportWorkspace" */ '../../views/nominal/NominalReportWorkspace')
);
const ReportForm = lazy(() => import(/* webpackChunkName: "ReportForm" */ '../../views/nominal/NominalReportForm'));
const NominalCostOfSaleForm = lazy(
  () => import(/* webpackChunkName: "NominalCostOfSaleForm" */ '../../views/nominal/NominalCostOfSaleForm')
);
const NominalTrialBalance = lazy(
  () => import(/* webpackChunkName: "NominalTrialBalance" */ '../../views/nominal/NominalTrialBalanceForm')
);
const NominalYearEnd = lazy(
  () => import(/* webpackChunkName: "NominalYearEnd" */ '../../views/nominal/NominalYearEndForm')
);
const NominalProfitLoss = lazy(
  () => import(/* webpackChunkName: "NominalProfitLoss" */ '../../views/nominal/NominalProfitLossForm')
);
const NominalBalanceSheet = lazy(
  () => import(/* webpackChunkName: "NominalBalanceSheet" */ '../../views/nominal/NominalBalanceSheetForm')
);

const ProductAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductAttributeConfigWorkspace" */ '../../views/product/ProductAttributeConfigWorkspace'
    )
);

const ProductCategoryAttributeConfigWorkspace = lazy(
  () => import('../../views/product/ProductCategoryAttributeConfigWorkspace')
);

const ProductAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "ProductAttributeConfigForm" */ '../../views/product/ProductAttributeConfigForm')
);

const MaterialForm = lazy(() => import(/* webpackChunkName: "MaterialForm" */ '../../views/product/MaterialForm'));

const SubMaterialForm = lazy(
  () => import(/* webpackChunkName: "SubMaterialForm" */ '../../views/product/SubMaterialForm')
);

const MaterialColourForm = lazy(
  () => import(/* webpackChunkName: "MaterialColourForm" */ '../../views/product/MaterialColourForm')
);

const ProductWarehouseDetailsWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductWarehouseDetailsWorkspace" */ '../../views/product/ProductWarehouseDetailsWorkspace'
    )
);

const ProductSupplierDetailsWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductSupplierDetailsWorkspace" */ '../../views/product/ProductSupplierDetailsWorkspace'
    )
);

const MaterialWorkspace = lazy(
  () => import(/* webpackChunkName: "MaterialWorkspace" */ '../../views/product/MaterialWorkspace')
);

const EprAggregateRecordWorkspace = lazy(
  () => import(/* webpackChunkName: "EprAggregateRecordWorkspace" */ '../../views/product/EprAggregateRecordWorkspace')
);

const ProductMaterialDetailWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductMaterialDetailWorkspace" */ '../../views/product/ProductMaterialDetailWorkspace'
    )
);

const SubMaterialWorkspace = lazy(
  () => import(/* webpackChunkName: "SubMaterialWorkspace" */ '../../views/product/SubMaterialWorkspace')
);

const MaterialColourWorkspace = lazy(
  () => import(/* webpackChunkName: "MaterialColourWorkspace" */ '../../views/product/MaterialColourWorkspace')
);

const StockErrorWorkspace = lazy(
  () => import(/* webpackChunkName: "StockErrorWorkspace" */ '../../views/product/StockErrorWorkspace')
);

const AssemblyAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "AssemblyAttributeConfigWorkspace" */ '../../views/product/AssemblyAttributeConfigWorkspace'
    )
);
const AssemblyAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "AssemblyAttributeConfigForm" */ '../../views/product/AssemblyAttributeConfigForm')
);
const AssemblyForm = lazy(() => import(/* webpackChunkName: "AssemblyForm" */ '../../views/product/AssemblyForm'));

const AssemblyAssignmentForm = lazy(
  () => import(/* webpackChunkName: "AssemblyAssignmentForm" */ '../../views/product/AssemblyAssignmentForm')
);

const AssemblyWorkspace = lazy(
  () => import(/* webpackChunkName: "AssemblyWorkspace" */ '../../views/product/AssemblyWorkspace')
);
const AssemblyRequirementDetailWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "AssemblyRequirementDetailWorkspace" */ '../../views/product/AssemblyRequirementDetailWorkspace'
    )
);
const AssemblyRequirementDetailForm = lazy(
  () =>
    import(/* webpackChunkName: "AssemblyRequirementDetailForm" */ '../../views/product/AssemblyRequirementDetailForm')
);
const AssemblyReservationForm = lazy(
  () => import(/* webpackChunkName: "AssemblyReservationForm" */ '../../views/product/AssemblyReservationForm')
);

const AssemblyReservationTreeForm = lazy(
  () =>
    import(/* webpackChunkName: "AssemblyReservationTreeSpace" */ '../../views/product/AssemblyReservationTreeSpace')
);

const AssemblyNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "AssemblyNoteTypeWorkspace" */ '../../views/product/AssemblyNoteTypeWorkspace')
);
const AssemblyNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "AssemblyNoteTypeForm" */ '../../views/product/AssemblyNoteTypeForm')
);
const ProductConfigForm = lazy(
  () => import(/* webpackChunkName: "ProductConfigForm" */ '../../views/product/ProductConfigForm')
);
const ProductWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductWorkspace" */ '../../views/product/ProductWorkspace')
);
const ProductTemplateWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductTemplateWorkspace" */ '../../views/product/ProductTemplateWorkspace')
);
const ProductForm = lazy(() => import(/* webpackChunkName: "ProductForm" */ '../../views/product/ProductForm'));
const ProductAuditForm = lazy(
  () => import(/* webpackChunkName: "ProductAuditForm" */ '../../views/product/ProductAuditForm')
);
const ProductNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductNoteTypeWorkspace" */ '../../views/product/ProductNoteTypeWorkspace')
);
const ProductNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "ProductNoteTypeForm" */ '../../views/product/ProductNoteTypeForm')
);
const PhysicalDimensionDescriptionForm = lazy(
  () =>
    import(
      /* webpackChunkName: "PhysicalDimensionDescriptionForm" */ '../../views/product/PhysicalDimensionDescriptionForm'
    )
);
const PhysicalDimensionDescriptionWorkspace = lazy(
  () => import('../../views/product/PhysicalDimensionDescriptionWorkspace')
);
const BinLocationWorkspace = lazy(
  () => import(/* webpackChunkName: "BinLocationWorkspace" */ '../../views/product/ProductsBinLocationWorkspace')
);
const BinLocationForm = lazy(
  () => import(/* webpackChunkName: "BinLocationForm" */ '../../views/product/ProductsBinLocationForm')
);
const BinLocationRangeForm = lazy(
  () => import(/* webpackChunkName: "BinLocationRangeForm" */ '../../views/product/ProductsBinLocationRangeForm')
);
const StockLotForm = lazy(() => import(/* webpackChunkName: "StockLotForm" */ '../../views/product/StockLotForm'));
const GoodsReceiptWorkspace = lazy(
  () => import(/* webpackChunkName: "GoodsReceiptWorkspace" */ '../../views/product/ProductsGoodsReceiptWorkspace')
);
const GoodsReceiptForm = lazy(
  () => import(/* webpackChunkName: "GoodsReceiptForm" */ '../../views/product/ProductsGoodsReceiptForm')
);
const LotAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "LotAttributeConfigWorkspace" */ '../../views/product/ProductsLotAttributeConfigWorkspace'
    )
);
const LotAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "LotAttributeConfigForm" */ '../../views/product/ProductsLotAttributeConfigForm')
);
const StockAdjustmentWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "StockAdjustmentWorkspace" */ '../../views/product/ProductsStockAdjustmentWorkspace')
);
const StockAdjustmentForm = lazy(
  () => import(/* webpackChunkName: "StockAdjustmentForm" */ '../../views/product/ProductsStockAdjustmentForm')
);
const StockAdjustmentReasonWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "StockAdjustmentReasonWorkspace" */ '../../views/product/ProductsStockAdjustmentReasonWorkspace'
    )
);
const StockAdjustmentReasonForm = lazy(
  () =>
    import(/* webpackChunkName: "StockAdjustmentReasonForm" */ '../../views/product/ProductsStockAdjustmentReasonForm')
);
const StockTransactionWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "StockTransactionWorkspace" */ '../../views/product/ProductsStockTransactionWorkspace')
);
const StockTransferWorkspace = lazy(
  () => import(/* webpackChunkName: "StockTransferWorkspace" */ '../../views/product/ProductsStockTransferWorkspace')
);
const StockTransferForm = lazy(
  () => import(/* webpackChunkName: "StockTransferForm" */ '../../views/product/ProductsStockTransferForm')
);
const StockTransferReasonWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "StockTransferReasonWorkspace" */ '../../views/product/ProductsStockTransferReasonWorkspace'
    )
);
const StockTransferReasonForm = lazy(
  () => import(/* webpackChunkName: "StockTransferReasonForm" */ '../../views/product/ProductsStockTransferReasonForm')
);
const WarehouseWorkspace = lazy(
  () => import(/* webpackChunkName: "WarehouseWorkspace" */ '../../views/product/ProductsWarehouseWorkspace')
);
const WarehouseForm = lazy(
  () => import(/* webpackChunkName: "WarehouseForm" */ '../../views/product/ProductsWarehouseForm')
);
const MinMaxLevelsForm = lazy(
  () => import(/* webpackChunkName: "MinMaxLevelsForm" */ '../../views/product/ProductMinMaxLevelsForm')
);
const MinMaxCalculationResultDetailsWorkspace = lazy(
  () => import('../../views/product/MinMaxCalculationResultDetailsWorkspace')
);
const CustomerPriceReviewLineWorkspace = lazy(() => import('../../views/customer/CustomerPriceReviewLineWorkspace'));
const SupplierPriceReviewLineWorkspace = lazy(() => import('../../views/supplier/SupplierPriceReviewLineWorkspace'));
const AutomatedCreditControlResultWorkspace = lazy(
  () => import('../../views/salesLedger/AutomatedCreditControlResultWorkspace')
);
const AutomatedCreditControlScheduledEmailWorkspace = lazy(
  () => import('../../views/salesLedger/AutomatedCreditControlScheduledEmailWorkspace')
);
const ProductsStockTakeProgressiveForm = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductsStockTakeProgressiveForm" */ '../../views/product/ProductsStockTakeProgressiveForm'
    )
);
const P4StockTakeProgressiveForm = lazy(
  () => import(/* webpackChunkName: "P4StockTakeProgressiveForm" */ '../../views/product/P4StockTakeProgressiveForm')
);
const ProductsStockTakeWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductsStockTakeWorkspace" */ '../../views/product/StockTakeWorkspace')
);
const LegacyProductsStockTakeWorkspace = lazy(async () => {
  const workspace = await import(
    /* webpackChunkName: "LegacyProductsStockTakeWorkspace" */ '../../views/product/StockTakeWorkspace'
  );
  return {
    default: workspace.LegacyStockTakeWorkspace,
  };
});
const ProductsStockTakeDirectForm = lazy(
  () => import(/* webpackChunkName: "ProductsStockTakeDirectForm" */ '../../views/product/ProductsStockTakeDirectForm')
);
const StockTakeLineWorkspace = lazy(
  () => import(/* webpackChunkName: "StockTakeLineWorkspace" */ '../../views/product/StockTakeLineWorkspace')
);
const StockTakeStatusWorkspace = lazy(
  () => import(/* webpackChunkName: "StockTakeStatusWorkspace" */ '../../views/product/StockTakeStatusWorkspace')
);
const StockTakeStatusForm = lazy(
  () => import(/* webpackChunkName: "StockTakeStatusForm" */ '../../views/product/StockTakeStatusForm')
);
const ProductCategoryForm = lazy(
  () => import(/* webpackChunkName: "ProductCategoryForm" */ '../../views/product/ProductCategoryForm')
);
const ProductCategoryTreespace = lazy(
  () => import(/* webpackChunkName: "ProductCategoryTreespace" */ '../../views/product/ProductCategoryTreespace')
);
const WebMediaFolderTreespace = lazy(
  () => import(/* webpackChunkName: "WebMediaFolderTreespace" */ '../../views/web/WebMediaFolderTreespace')
);
const ProductGlobalAmendForm = lazy(
  () => import(/* webpackChunkName: "ProductGlobalAmendForm" */ '../../views/product/ProductGlobalAmendForm')
);
const ProductGlobalAmendResultsWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductGlobalAmendResultsWorkspace" */ '../../views/product/ProductGlobalAmendResultsWorkspace'
    )
);
const ProductAllocationForm = lazy(
  () => import(/* webpackChunkName: "ProductAllocationForm" */ '../../views/product/ProductAllocationForm')
);
const ProductPriceMatrixWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductPriceMatrixWorkspace" */ '../../views/product/ProductPriceMatrixWorkspace')
);
const ProductPriceMatrixForm = lazy(
  () => import(/* webpackChunkName: "ProductPriceMatrixForm" */ '../../views/product/ProductPriceMatrixForm')
);
const ProductPriceMatrixPreviewWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ProductPriceMatrixPreviewWorkspace" */ '../../views/product/ProductPriceMatrixPreviewWorkspace'
    )
);
const ProductsCertificateWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "ProductsCertificateWorkspace" */ '../../views/product/ProductsCertificateWorkspace')
);
const ProductsCertificateForm = lazy(
  () => import(/* webpackChunkName: "ProductsCertificateForm" */ '../../views/product/ProductsCertificateForm')
);
const ProductAllocationWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductAllocationsWorkspace" */ '../../views/product/ProductAllocationWorkspace')
);
const BulkAllocationResultWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "BulkAllocationResultWorkspace" */ '../../views/product/BulkAllocationResultWorkspace')
);
const ReceiptLabelDocumentPrintForm = lazy(
  () =>
    import(/* webpackChunkName: "ReceiptLabelDocumentPrintForm" */ '../../views/product/ReceiptLabelDocumentPrintForm')
);
const StockAdjustmentLabelDocumentPrintForm = lazy(
  () =>
    import(
      /* webpackChunkName: "StockAdjustmentLabelDocumentPrintForm" */ '../../views/product/StockAdjustmentLabelDocumentPrintForm'
    )
);
const ProductUsageWorkspace = lazy(
  () => import(/* webpackChunkName: "ProductUsageWorkspace" */ '../../views/product/ProductUsageWorkspace')
);
const TraceabilityForm = lazy(
  () => import(/* webpackChunkName: "TraceabilityForm" */ '../../views/product/TraceabilityForm')
);
const GoodsReceiptLineWorkspace = lazy(
  () => import(/* webpackChunkName: "GoodsReceiptLineWorkspace" */ '../../views/product/GoodsReceiptLineWorkspace')
);
const DeadStockPrintForm = lazy(
  () => import(/* webpackChunkName: "DeadStockPrintForm" */ '../../views/product/DeadStockPrintForm')
);
const ProductBulkImageImportForm = lazy(
  () => import(/* webpackChunkName: "ProductBulkImageImportForm" */ '../../views/product/ProductBulkImageImportForm')
);
const PromotionalMessageWorkspace = lazy(
  () => import(/* webpackChunkName: "PromotionalMessageWorkspace" */ '../../views/product/PromotionalMessageWorkspace')
);
const PromotionalMessageForm = lazy(
  () => import(/* webpackChunkName: "PromotionalMessageForm" */ '../../views/product/PromotionalMessageForm')
);
const LuckinsItemWorkspace = lazy(
  () => import(/* webpackChunkName: "LuckinsItemWorkspace" */ '../../views/product/LuckinsItemWorkspace')
);
const LuckinsVatRateWorkspace = lazy(
  () => import(/* webpackChunkName: "LuckinsVatRateWorkspace" */ '../../views/product/LuckinsVatRateWorkspace')
);
const LuckinsVatRateForm = lazy(
  () => import(/* webpackChunkName: "LuckinsVatRateForm" */ '../../views/product/LuckinsVatRateForm')
);
const LuckinsSupplierWorkspace = lazy(
  () => import(/* webpackChunkName: "LuckinsSupplierWorkspace" */ '../../views/product/LuckinsSupplierWorkspace')
);
const LuckinsSupplierForm = lazy(
  () => import(/* webpackChunkName: "LuckinsSupplierForm" */ '../../views/product/LuckinsSupplierForm')
);
const LuckinsConfigForm = lazy(
  () => import(/* webpackChunkName: "LuckinsConfigForm" */ '../../views/product/LuckinsConfigForm')
);
const LuckinsImportWorkspace = lazy(
  () => import(/* webpackChunkName: "LuckinsImportWorkspace" */ '../../views/product/LuckinsImportWorkspace')
);
const LuckinsItemImportFailureWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "LuckinsItemImportFailureWorkspace" */ '../../views/product/LuckinsItemImportFailureWorkspace'
    )
);
const PromotionalPriceForm = lazy(
  () => import(/* webpackChunkName: "PromotionalPriceForm" */ '../../views/product/PromotionalPriceForm')
);
const PromotionalPriceWorkspace = lazy(
  () => import(/* webpackChunkName: "PromotionalPriceWorkspace" */ '../../views/product/PromotionalPriceWorkspace')
);

const SupplierAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "SupplierAttributeConfigWorkspace" */ '../../views/supplier/SupplierAttributeConfigWorkspace'
    )
);
const SupplierAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "SupplierAttributeConfigForm" */ '../../views/supplier/SupplierAttributeConfigForm')
);
const SupplierContactAttributeConfigWorkspace = lazy(
  () => import('../../views/supplier/SupplierContactAttributeConfigWorkspace')
);
const SupplierContactAttributeConfigForm = lazy(
  () => import('../../views/supplier/SupplierContactAttributeConfigForm')
);
const SupplierConfigForm = lazy(
  () => import(/* webpackChunkName: "SupplierConfigForm" */ '../../views/supplier/SupplierConfigForm')
);
const SupplierNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "SupplierNoteTypeWorkspace" */ '../../views/supplier/SupplierNoteTypeWorkspace')
);
const SupplierNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "SupplierNoteTypeForm" */ '../../views/supplier/SupplierNoteTypeForm')
);
const SupplierWorkspace = lazy(
  () => import(/* webpackChunkName: "SupplierWorkspace" */ '../../views/supplier/SupplierWorkspace')
);
const SupplierForm = lazy(() => import(/* webpackChunkName: "SupplierForm" */ '../../views/supplier/SupplierForm'));
const SupplierBankDetailsWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "SupplierBankDetailsWorkspace" */ '../../views/supplier/SupplierBankDetailsWorkspace')
);
const SupplierBankDetailsForm = lazy(
  () => import(/* webpackChunkName: "SupplierBankDetailsForm" */ '../../views/supplier/SupplierBankDetailsForm')
);
const SupplierAuditForm = lazy(
  () => import(/* webpackChunkName: "SupplierAuditForm" */ '../../views/supplier/SupplierAuditForm')
);
const SupplierGlobalAmendForm = lazy(
  () => import(/* webpackChunkName: "SupplierGlobalAmendForm" */ '../../views/supplier/SupplierGlobalAmendForm')
);
const SupplierGlobalAmendResultsWorkspace = lazy(
  () => import('../../views/supplier/SupplierGlobalAmendResultsWorkspace')
);
const QuickPurchaseInvoiceForm = lazy(
  () => import(/* webpackChunkName: "QuickPurchaseInvoiceForm" */ '../../views/supplier/QuickPurchaseInvoiceForm')
);
const QuickPurchaseCreditForm = lazy(
  () => import(/* webpackChunkName: "QuickPurchaseCreditForm" */ '../../views/supplier/QuickPurchaseCreditForm')
);
const PurchaseInvoiceWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseInvoiceWorkspace" */ '../../views/supplier/PurchaseInvoiceWorkspace')
);
const PurchaseInvoiceForm = lazy(
  () => import(/* webpackChunkName: "PurchaseInvoiceForm" */ '../../views/supplier/PurchaseInvoiceForm')
);
const PurchaseTransactionWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "PurchaseTransactionWorkspace" */ '../../views/supplier/PurchaseTransactionWorkspace')
);
const PurchaseTransactionEnquiryForm = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseTransactionEnquiryForm" */ '../../views/supplier/PurchaseTransactionEnquiryForm'
    )
);
const SupplierAccountStatusWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "SupplierAccountStatusWorkspace" */ '../../views/supplier/SupplierAccountStatusWorkspace'
    )
);
const SupplierAccountStatusForm = lazy(
  () => import(/* webpackChunkName: "SupplierAccountStatusForm" */ '../../views/supplier/SupplierAccountStatusForm')
);
const PurchasePaymentTermWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "PurchasePaymentTermWorkspace" */ '../../views/supplier/PurchasePaymentTermWorkspace')
);
const PurchasePaymentTermForm = lazy(
  () => import(/* webpackChunkName: "PurchasePaymentTermForm" */ '../../views/supplier/PurchasePaymentTermForm')
);
const PurchaseOpeningBalanceWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseOpeningBalanceWorkspace" */ '../../views/supplier/PurchaseOpeningBalanceWorkspace'
    )
);
const PurchaseOpeningBalanceForm = lazy(
  () => import(/* webpackChunkName: "PurchaseOpeningBalanceForm" */ '../../views/supplier/PurchaseOpeningBalanceForm')
);
const PurchaseCreditWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseCreditWorkspace" */ '../../views/supplier/PurchaseCreditWorkspace')
);
const PurchaseCreditForm = lazy(
  () => import(/* webpackChunkName: "PurchaseCreditForm" */ '../../views/supplier/PurchaseCreditForm')
);
const PurchaseAllocationWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseAllocationWorkspace" */ '../../views/supplier/PurchaseAllocationWorkspace')
);
const PurchaseAllocationForm = lazy(
  () => import(/* webpackChunkName: "PurchaseAllocationForm" */ '../../views/supplier/PurchaseAllocationForm')
);
const PurchaseVatOnlyWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseVatOnlyWorkspace" */ '../../views/supplier/PurchaseVatOnlyWorkspace')
);
const PurchaseVatOnlyForm = lazy(
  () => import(/* webpackChunkName: "PurchaseVatOnlyForm" */ '../../views/supplier/PurchaseVatOnlyForm')
);
const PurchasePaymentWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchasePaymentWorkspace" */ '../../views/supplier/PurchasePaymentWorkspace')
);
const PurchasePaymentForm = lazy(
  () => import(/* webpackChunkName: "PurchasePaymentForm" */ '../../views/supplier/PurchasePaymentForm')
);
const PurchaseReconciliationForm = lazy(
  () => import(/* webpackChunkName: "PurchaseReconciliationForm" */ '../../views/supplier/PurchaseReconciliationForm')
);
const PurchasePaymentMethodWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchasePaymentMethodWorkspace" */ '../../views/supplier/PurchasePaymentMethodWorkspace'
    )
);
const PurchasePaymentMethodForm = lazy(
  () => import(/* webpackChunkName: "PurchasePaymentMethodForm" */ '../../views/supplier/PurchasePaymentMethodForm')
);
const SuggestedPaymentWorkspace = lazy(
  () => import(/* webpackChunkName: "SuggestedPaymentWorkspace" */ '../../views/supplier/SuggestedPaymentWorkspace')
);
const SuggestedPaymentForm = lazy(
  () => import(/* webpackChunkName: "SuggestedPaymentForm" */ '../../views/supplier/SuggestedPaymentForm')
);
const SupplierPriceMatrixWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "SupplierPriceMatrixWorkspace" */ '../../views/supplier/SupplierPriceMatrixWorkspace')
);
const SupplierPriceMatrixPreviewWorkspace = lazy(
  () => import('../../views/supplier/SupplierPriceMatrixPreviewWorkspace')
);
const SupplierPriceMatrixForm = lazy(
  () => import(/* webpackChunkName: "SupplierPriceMatrixForm" */ '../../views/supplier/SupplierPriceMatrixForm')
);
const PurchaseControlTotalsForm = lazy(
  () => import(/* webpackChunkName: "PurchaseControlTotalsForm" */ '../../views/supplier/PurchaseControlTotalsForm')
);
const SupplierPriceListWorkspace = lazy(
  () => import(/* webpackChunkName: "SupplierPriceListWorkspace" */ '../../views/supplier/SupplierPriceListWorkspace')
);
const PurchaseRefundWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseRefundWorkspace" */ '../../views/supplier/PurchaseRefundWorkspace')
);
const PurchaseRefundForm = lazy(
  () => import(/* webpackChunkName: "PurchaseRefundForm" */ '../../views/supplier/PurchaseRefundForm')
);
const VendorRatingWorkspace = lazy(
  () => import(/* webpackChunkName: "VendorRatingWorkspace" */ '../../views/supplier/VendorRatingWorkspace')
);
const VendorRatingForm = lazy(
  () => import(/* webpackChunkName: "VendorRatingForm" */ '../../views/supplier/VendorRatingForm')
);
const MatchPurchaseOrdersForm = lazy(
  () => import(/* webpackChunkName: "MatchPurchaseOrdersForm" */ '../../views/supplier/MatchPurchaseOrdersForm')
);

const CustomerAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerAttributeConfigWorkspace" */ '../../views/customer/CustomerAttributeConfigWorkspace'
    )
);
const CustomerAttributeConfigForm = lazy(
  () => import(/* webpackChunkName: "CustomerAttributeConfigForm" */ '../../views/customer/CustomerAttributeConfigForm')
);
const CustomerContactAttributeConfigWorkspace = lazy(
  () => import('../../views/customer/CustomerContactAttributeConfigWorkspace')
);
const CustomerContactAttributeConfigForm = lazy(
  () => import('../../views/customer/CustomerContactAttributeConfigForm')
);
const CustomerConfigForm = lazy(
  () => import(/* webpackChunkName: "CustomerConfigForm" */ '../../views/customer/CustomerConfigForm')
);
const CustomerWorkspace = lazy(
  () => import(/* webpackChunkName: "CustomerWorkspace" */ '../../views/customer/CustomerWorkspace')
);
const CreditControlWorkspace = lazy(
  () => import(/* webpackChunkName: "CreditControlWorkspace" */ '../../views/customer/CreditControlWorkspace')
);
const CustomerContactWorkspace = lazy(
  () => import(/* webpackChunkName: "CustomerContactWorkspace" */ '../../views/customer/CustomerContactWorkspace')
);
const CustomerGlobalContactWorkspace = lazy(
  () => import(/* webpackChunkName: "CustomerContactWorkspace" */ '../../views/customer/CustomerGlobalContactWorkspace')
);
const CustomerGlobalContactForm = lazy(
  () => import(/* webpackChunkName: "CustomerGlobalContactForm" */ '../../views/customer/CustomerGlobalContactForm')
);
const CustomerForm = lazy(() => import(/* webpackChunkName: "CustomerForm" */ '../../views/customer/CustomerForm'));
const CustomerAuditForm = lazy(
  () => import(/* webpackChunkName: "CustomerAuditForm" */ '../../views/customer/CustomerAuditForm')
);
const CustomerPriceListWorkspace = lazy(
  () => import(/* webpackChunkName: "CustomerPriceListWorkspace" */ '../../views/customer/CustomerPriceListWorkspace')
);
const CustomerPriceReviewForm = lazy(
  () => import(/* webpackChunkName: "CustomerPriceReviewForm" */ '../../views/customer/CustomerPriceReviewForm')
);
const SupplierPriceReviewForm = lazy(
  () => import(/* webpackChunkName: "SupplierPriceReviewForm" */ '../../views/supplier/SupplierPriceReviewForm')
);
const CustomerNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "CustomerNoteTypeWorkspace" */ '../../views/customer/CustomerNoteTypeWorkspace')
);
const CustomerNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "CustomerNoteTypeForm" */ '../../views/customer/CustomerNoteTypeForm')
);
const SpecialPricesWorkspace = lazy(
  () => import(/* webpackChunkName: "SpecialPricesWorkspace" */ '../../views/customer/CustomerSpecialPricesWorkspace')
);
const SpecialPricesForm = lazy(
  () => import(/* webpackChunkName: "SpecialPricesForm" */ '../../views/customer/CustomerSpecialPricesForm')
);
const CustomerGlobalAmendForm = lazy(
  () => import(/* webpackChunkName: "CustomerGlobalAmendForm" */ '../../views/customer/CustomerGlobalAmendForm')
);
const CustomerGlobalAmendResultsWorkspace = lazy(
  () => import('../../views/customer/CustomerGlobalAmendResultsWorkspace')
);
const AccountStatusWorkspace = lazy(
  () => import(/* webpackChunkName: "AccountStatusWorkspace" */ '../../views/customer/CustomerAccountStatusWorkspace')
);
const AccountStatusForm = lazy(
  () => import(/* webpackChunkName: "AccountStatusForm" */ '../../views/customer/CustomerAccountStatusForm')
);
const CreditAgencyWorkspace = lazy(
  () => import(/* webpackChunkName: "CreditAgencyWorkspace" */ '../../views/customer/CreditAgencyWorkspace')
);
const CreditAgencyForm = lazy(
  () => import(/* webpackChunkName: "CreditAgencyForm" */ '../../views/customer/CreditAgencyForm')
);
const CustomerPriceSourceForm = lazy(
  () => import(/* webpackChunkName: "CustomerPriceSourceForm" */ '../../views/customer/CustomerPriceSourceForm')
);
const CustomerPriceSourceWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "CustomerPriceSourceWorkspace" */ '../../views/customer/CustomerPriceSourceWorkspace')
);
const CustomerAccountManagementGroupWorkSpace = lazy(
  () => import('../../views/customer/CustomerAccountManagementGroupWorkspace')
);
const CustomerAccountManagementGroupForm = lazy(
  () => import('../../views/customer/CustomerAccountManagementGroupForm')
);
const CustomerPriceMatrixWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "CustomerPriceMatrixWorkspace" */ '../../views/customer/CustomerPriceMatrixWorkspace')
);
const CustomerPriceMatrixPreviewWorkspace = lazy(
  () => import('../../views/customer/CustomerPriceMatrixPreviewWorkspace')
);
const CustomerPriceMatrixForm = lazy(
  () => import(/* webpackChunkName: "CustomerPriceMatrixForm" */ '../../views/customer/CustomerPriceMatrixForm')
);
const SalesControlTotalsForm = lazy(
  () => import(/* webpackChunkName: "SalesControlTotalsForm" */ '../../views/customer/SalesControlTotalsForm')
);
const CustomerBudgetProfileForm = lazy(
  () => import(/* webpackChunkName: "CustomerBudgetProfileForm" */ '../../views/customer/CustomerBudgetProfileForm')
);
const CustomerBudgetProfileWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerBudgetProfileWorkspace" */ '../../views/customer/CustomerBudgetProfileWorkspace'
    )
);

const ContactJobTitleWorkspace = lazy(
  () => import(/* webpackChunkName: "ContactJobTitleWorkspace" */ '../../views/common/ContactJobTitleWorkspace')
);
const JobTitleForm = lazy(() => import(/* webpackChunkName: "JobTitleForm" */ '../../views/common/JobTitleForm'));

const PurchaseOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseOrderWorkspace" */ '../../views/purchases/PurchaseOrderWorkspace')
);
const PurchaseOrderForm = lazy(
  () => import(/* webpackChunkName: "PurchaseOrderForm" */ '../../views/purchases/PurchaseOrderForm')
);
const PurchaseOrderLineWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseOrderLineWorkspace" */ '../../views/purchases/PurchaseOrderLineWorkspace')
);
const PurchaseAttributeConfigForm = lazy(
  () =>
    import(/* webpackChunkName: "PurchaseAttributeConfigForm" */ '../../views/purchases/PurchaseAttributeConfigForm')
);
const PurchaseAttributeConfigWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseAttributeConfigWorkspace" */ '../../views/purchases/PurchaseAttributeConfigWorkspace'
    )
);
const PurchaseNoteTypeWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseNoteTypeWorkspace" */ '../../views/purchases/PurchaseNoteTypeWorkspace')
);
const PurchaseNoteTypeForm = lazy(
  () => import(/* webpackChunkName: "PurchaseNoteTypeForm" */ '../../views/purchases/PurchaseNoteTypeForm')
);
const PurchaseOrderReceiptForm = lazy(
  () => import(/* webpackChunkName: "PurchaseOrderReceiptForm" */ '../../views/purchases/PurchaseOrderReceiptForm')
);
const PurchaseOrderCancelReceiptForm = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseOrderCancelReceiptForm" */ '../../views/purchases/PurchaseOrderCancelReceiptForm'
    )
);
const PurchaseEdiConfigWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseEdiConfigWorkspace" */ '../../views/purchases/PurchaseEdiConfigWorkspace')
);

const PurchaseEdiConfigForm = lazy(
  () => import(/* webpackChunkName: "PurchaseEdiConfigForm" */ '../../views/purchases/PurchaseEdiConfigForm')
);

const PurchaseDocumentConfigForm = lazy(
  () => import(/* webpackChunkName: "PurchaseDocumentConfigForm" */ '../../views/purchases/PurchaseDocumentConfigForm')
);
const PurchaseOrderDocumentPrintForm = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseOrderDocumentPrintForm" */ '../../views/purchases/PurchaseOrderDocumentPrintForm'
    )
);
const DebitNoteDocumentPrintForm = lazy(
  () => import(/* webpackChunkName: "DebitNoteDocumentPrintForm" */ '../../views/purchases/DebitNoteDocumentPrintForm')
);
const PurchaseRequirementWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "PurchaseRequirementWorkspace" */ '../../views/purchases/PurchaseRequirementWorkspace')
);
const PurchaseRequirementDetailWorkspace = lazy(
  () => import('../../views/purchases/PurchaseRequirementDetailWorkspace')
);
const PurchaseRequirementDetailPreviewWorkspace = lazy(
  () => import('../../views/purchases/PurchaseRequirementDetailPreviewWorkspace')
);
const PurchaseRequirementDetailForm = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseRequirementDetailForm" */ '../../views/purchases/PurchaseRequirementDetailForm'
    )
);
const PurchaseRequirementSingleGenerationForm = lazy(
  () => import('../../views/purchases/PurchaseRequirementGenerationForm')
);
const PurchaseAssignmentForm = lazy(
  () => import(/* webpackChunkName: "PurchaseAssignmentForm" */ '../../views/purchases/PurchaseAssignmentForm')
);
const PurchaseConfigForm = lazy(
  () => import(/* webpackChunkName: "PurchaseConfigForm" */ '../../views/purchases/PurchaseConfigForm')
);
const PurchaseCostWorkspace = lazy(
  () => import(/* webpackChunkName: "PurchaseCostWorkspace" */ '../../views/purchases/PurchaseCostTemplateWorkspace')
);
const PurchaseCostForm = lazy(
  () => import(/* webpackChunkName: "PurchaseCostForm" */ '../../views/purchases/PurchaseCostTemplateForm')
);
const PurchaseOrderLineAuditForm = lazy(
  () => import(/* webpackChunkName: "PurchaseOrderLineAuditForm" */ '../../views/purchases/PurchaseOrderLineAuditForm')
);
const SupplierReturnWorkspace = lazy(
  () => import(/* webpackChunkName: "SupplierReturnWorkspace" */ '../../views/purchases/SupplierReturnWorkspace')
);
const SupplierReturnForm = lazy(
  () => import(/* webpackChunkName: "SupplierReturnForm" */ '../../views/purchases/SupplierReturnForm')
);

const BusinessRuleWorkspace = lazy(
  () => import(/* webpackChunkName: "BusinessRuleWorkspace" */ '../../views/schedules/BusinessRuleWorkspace')
);
const BusinessRuleForm = lazy(
  () => import(/* webpackChunkName: "BusinessRuleForm" */ '../../views/schedules/BusinessRuleForm')
);

const InvoiceWorkspace = lazy(
  () => import(/* webpackChunkName: "InvoiceWorkspace" */ '../../views/salesLedger/SalesInvoiceWorkspace')
);
const InvoiceLineWorkspace = lazy(
  () => import(/* webpackChunkName: "InvoiceLineWorkspace" */ '../../views/salesLedger/SalesInvoiceLineWorkspace')
);
const InvoiceForm = lazy(
  () => import(/* webpackChunkName: "InvoiceForm" */ '../../views/salesLedger/SalesInvoiceForm')
);
const CreditWorkspace = lazy(
  () => import(/* webpackChunkName: "CreditWorkspace" */ '../../views/salesLedger/SalesCreditWorkspace')
);
const CreditLineWorkspace = lazy(
  () => import(/* webpackChunkName: "CreditLineWorkspace" */ '../../views/salesLedger/SalesCreditLineWorkspace')
);
const ReturnOrderWorkspace = lazy(
  () => import(/* webpackChunkName: "ReturnOrderWorkspace" */ '../../views/salesLedger/ReturnOrderWorkspace')
);
const CreditForm = lazy(
  () => import(/* webpackChunkName: "CreditForm" */ '../../views/salesLedger/salesCredit/SalesCreditForm')
);
const SalesPaymentTermWorkspace = lazy(
  () => import(/* webpackChunkName: "SalesPaymentTermWorkspace" */ '../../views/salesLedger/SalesPaymentTermWorkspace')
);
const SalesPaymentTermForm = lazy(
  () => import(/* webpackChunkName: "SalesPaymentTermForm" */ '../../views/salesLedger/SalesPaymentTermForm')
);
const ReceiptWorkspace = lazy(
  () => import(/* webpackChunkName: "ReceiptWorkspace" */ '../../views/salesLedger/SalesReceiptWorkspace')
);
const ReceiptForm = lazy(
  () => import(/* webpackChunkName: "ReceiptForm" */ '../../views/salesLedger/SalesReceiptForm')
);
const RefundWorkspace = lazy(
  () => import(/* webpackChunkName: "RefundWorkspace" */ '../../views/salesLedger/SalesRefundWorkspace')
);
const RefundForm = lazy(() => import(/* webpackChunkName: "RefundForm" */ '../../views/salesLedger/SalesRefundForm'));
const SalesTransactionWorkspace = lazy(
  () => import(/* webpackChunkName: "SalesTransactionWorkspace" */ '../../views/salesLedger/SalesTransactionWorkspace')
);
const TransactionForm = lazy(
  () => import(/* webpackChunkName: "TransactionForm" */ '../../views/salesLedger/SalesTransactionForm')
);
const AllocationWorkspace = lazy(
  () => import(/* webpackChunkName: "AllocationWorkspace" */ '../../views/salesLedger/SalesAllocationWorkspace')
);
const AllocationForm = lazy(
  () => import(/* webpackChunkName: "AllocationForm" */ '../../views/salesLedger/SalesAllocationForm')
);
const SalesReconciliationForm = lazy(
  () => import(/* webpackChunkName: "SalesReconciliationForm" */ '../../views/salesLedger/SalesReconciliationForm')
);
const SalesOpeningBalanceWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "SalesOpeningBalanceWorkspace" */ '../../views/salesLedger/SalesOpeningBalanceWorkspace'
    )
);
const SalesOpeningBalanceForm = lazy(
  () => import(/* webpackChunkName: "SalesOpeningBalanceForm" */ '../../views/salesLedger/SalesOpeningBalanceForm')
);
const BadDebtWorkspace = lazy(
  () => import(/* webpackChunkName: "BadDebtWorkspace" */ '../../views/salesLedger/SalesBadDebtWorkspace')
);
const BadDebtForm = lazy(
  () => import(/* webpackChunkName: "BadDebtForm" */ '../../views/salesLedger/SalesBadDebtForm')
);
const InvoiceConfigForm = lazy(
  () => import(/* webpackChunkName: "InvoiceConfigForm" */ '../../views/salesLedger/InvoiceConfigForm')
);
const SalesAnalysis = lazy(
  () => import(/* webpackChunkName: "SalesAnalysis" */ '../../views/salesLedger/SalesAnalysisForm')
);
const CreditReasonWorkspace = lazy(
  () => import(/* webpackChunkName: "CreditReasonWorkspace" */ '../../views/salesLedger/SalesCreditReasonWorkspace')
);
const CreditReasonForm = lazy(
  () => import(/* webpackChunkName: "CreditReasonForm" */ '../../views/salesLedger/SalesCreditReasonForm')
);
const SalesTransactionEnquiryForm = lazy(
  () =>
    import(/* webpackChunkName: "SalesTransactionEnquiryForm" */ '../../views/salesLedger/SalesTransactionEnquiryForm')
);
const VatOnlyWorkspace = lazy(
  () => import(/* webpackChunkName: "VatOnlyWorkspace" */ '../../views/salesLedger/SalesVatOnlyWorkspace')
);
const VatOnlyForm = lazy(
  () => import(/* webpackChunkName: "VatOnlyForm" */ '../../views/salesLedger/SalesVatOnlyForm')
);
const TradeCounterForm = lazy(
  () => import(/* webpackChunkName: "TradeCounterForm" */ '../../views/salesLedger/tradeCounter/TradeCounterForm')
);
const PayByLinkWorkspace = lazy(
  () => import(/* webpackChunkName: "PayByLinkWorkspace" */ '../../views/salesLedger/PayByLinkWorkspace')
);
const AutomatedCreditControlWorkflowWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "PayByLinkWorkspace" */ '../../views/salesLedger/AutomatedCreditControlWorkflowWorkspace'
    )
);
const AutomatedCreditControlWorkflowForm = lazy(
  () =>
    import(/* webpackChunkName: "PayByLinkWorkspace" */ '../../views/salesLedger/AutomatedCreditControlWorkflowForm')
);
const DirectDebitRequestGenerationForm = lazy(
  () =>
    import(
      /* webpackChunkName: "DirectDebitRequestGenerationForm" */ '../../views/customer/DirectDebitRequestGenerationForm'
    )
);
const SalesLedgerJournalWorkspace = lazy(
  () =>
    import(/* webpackChunkName: "SalesLedgerJournalWorkspace" */ '../../views/salesLedger/SalesLedgerJournalWorkspace')
);
const SalesLedgerJournalForm = lazy(
  () => import(/* webpackChunkName: "SalesLedgerJournalForm" */ '../../views/salesLedger/SalesLedgerJournalForm')
);
const PurchaseLedgerJournalWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseLedgerJournalWorkspace" */ '../../views/purchases/PurchaseLedgerJournalWorkspace'
    )
);
const PurchaseLedgerJournalForm = lazy(
  () => import(/* webpackChunkName: "PurchaseLedgerJournalForm" */ '../../views/purchases/PurchaseLedgerJournalForm')
);
const JobReportForm = lazy(() => import(/* webpackChunkName: "JobReportForm" */ '../../views/system/JobReportForm'));
const JobReportWorkspace = lazy(
  () => import(/* webpackChunkName: "JobReportWorkspace" */ '../../views/system/JobReportWorkspace')
);
const BinReplenishmentHeaderWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "BinReplenishmentHeaderWorkspace" */ '../../views/product/BinReplenishmentHeaderWorkspace'
    )
);
const BinReplenishmentDetailWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "BinReplenishmentDetailWorkspace" */ '../../views/product/BinReplenishmentDetailWorkspace'
    )
);
const ReferenceAllocationFailureWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ReferenceAllocationFailureWorkspace" */ '../../views/common/ReferenceAllocationFailureWorkspace'
    )
);
const ConsolidatedPickWorkspace = lazy(
  () => import(/* webpackChunkName: "ConsolidatedPickWorkspace" */ '../../views/sales/ConsolidatedPickWorkspace')
);
const ConsolidatedPickForm = lazy(
  () => import(/* webpackChunkName: "ConsolidatedPickForm" */ '../../views/sales/ConsolidatedPickForm')
);
const ConsolidatedPickGroupWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "ConsolidatedPickGroupWorkspace" */ '../../views/product/ConsolidatedPickGroupWorkspace'
    )
);
const ConsolidatedPickGroupForm = lazy(
  () => import(/* webpackChunkName: "ConsolidatedPickGroupForm" */ '../../views/product/ConsolidatedPickGroupForm')
);
const Hauliers = lazy(() => import(/* webpackChunkName: "Hauliers" */ '../../views/sales/HaulierWorkspace'));
const HaulierForm = lazy(() => import(/* webpackChunkName: "HaulierForm" */ '../../views/sales/HaulierForm'));

export type IOptions = {
  Company?: IMenuSection;
  Customers?: IMenuSection<{ AutomatedCreditControl: IMenuSection }>;
  Products?: IMenuSection<
    | { Configuration?: IMenuSection }
    | { Luckins: IMenuSection }
    | { Assembly: IMenuSection }
    | { StockControl: IMenuSection }
    | { EPR: IMenuSection }
  >;
  Suppliers?: IMenuSection;
  Sales?: IMenuSection<
    | { Configuration?: IMenuSection }
    | { CustomerPricing: IMenuSection }
    | { Channel: IMenuSection }
    | { Picking: IMenuSection }
  >;
  Diary?: IMenuSection;
  Nominal?: IMenuSection<{ FinancialReports: IMenuSection }>;
  Vat?: IMenuSection;
  Bank?: IMenuSection;
  Purchasing?: IMenuSection<{ SupplierPricing: IMenuSection }>;
  Web?: IMenuSection;
  Reports?: IMenuSection<
    | { Financial: Array<IMenuEntry> }
    | { Customers: Array<IMenuEntry> }
    | { Suppliers: Array<IMenuEntry> }
    | { Vat: Array<IMenuEntry> }
    | { Products: Array<IMenuEntry> }
  >;
  System?: IMenuSection;
};

type ISystemMenuList = { menu: { [screenReferenceKey: string]: IScreen }; folders: { [key: `${number}`]: IFolder } };

const createFilter = (fieldName: string, field: string | undefined): IFilterInput => {
  return {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: fieldName,
      value: field,
    },
  };
};

const createComplexFilter = (key: string, value: string): IFilterInput | null => {
  if (key === 'dateStart') {
    return {
      filterOperation: FilterOperation.GreaterThanEqual,
      filterParameters: {
        field: 'documentDate',
        value: value,
      },
    };
  } else if (key === 'dateEnd') {
    return {
      filterOperation: FilterOperation.LessThanEqual,
      filterParameters: {
        field: 'documentDate',
        value: value,
      },
    };
  }
  return null;
};

const createJobFilterWorkspace = (WorkspaceComponent: JSXElementConstructor<any>) => {
  return (props: RouteComponentProps<any>) => {
    return <WorkspaceComponent predefinedFilters={[createFilter('jobId', props.match.params.jobid)]} {...props} />;
  };
};

const renderBulkAllocationResultsWorkspace = (props: RouteComponentProps<any>) => {
  return (
    <BulkAllocationResultWorkspace
      predefinedFilters={[createFilter('lastJobId', props.match.params.jobid), createFilter('cancelled', 'false')]}
      {...props}
    />
  );
};

const renderPurchaseRequirementDetailPreviewWorkspace = (props: RouteComponentProps<any>) => {
  return (
    <PurchaseRequirementDetailPreviewWorkspace
      predefinedFilters={[createFilter('jobId', props.match.params.jobid)]}
      {...props}
      fromPreview={true}
    />
  );
};

const createPredefinedFilterWorkspace = (WorkspaceComponent: JSXElementConstructor<any>) => {
  return (props: RouteComponentProps<any>) => {
    // Filters are passed as URL parameters
    const searchParams: any = new URLSearchParams(props.location.search);
    const filters: Array<IFilterInput> = [];
    for (const [key, value] of searchParams) {
      const filter = createComplexFilter(key, value);
      if (filter !== null) {
        filters.push(filter);
      }
    }

    return <WorkspaceComponent predefinedFilters={filters} {...props} />;
  };
};

/**
 * Helper function for configuring menu entries.
 * @param entity - Entity for the form. Should contain a title, formUrl and a screen reference.
 * @returns - a new menu entry
 */
const formFieldsFromEntity = (entity: any): IMenuEntry => {
  const { formUrl, permissionLabel, formTitle, actions, shortTitle, operations, screenReference } = entity;

  return {
    title: formTitle,
    shortTitle: shortTitle,
    url: formUrl,
    screenReference: screenReference,
    actions: actions,
    permissionLabel: permissionLabel,
    operations: operations === 0 ? 0 : operations || ALLOW_VIEW | ALLOW_ADD | ALLOW_EDIT | ALLOW_DELETE,
  } as IMenuEntry;
};

/**
 * Helper function for configuring menu entries.
 * @param entity - Entity for the form. Should contain a formUrl and a screen reference.
 * @param suffix - URL Suffix - Default of '' since this is rarely used.
 * @returns - a new menu entry
 */
const workspaceFieldsFromEntity = (entity: any, suffix: string = ''): IMenuEntry => {
  if (!entity) {
    console.trace();
  }

  const { actions, title, shortTitle, permissionLabel, operations, screenReference, workspaceUrl } = entity;

  return {
    title: title,
    shortTitle: shortTitle,
    url: `${workspaceUrl}${suffix}`,
    screenReference: screenReference,
    actions: actions,
    permissionLabel: permissionLabel,
    operations: operations === 0 ? 0 : operations || ALLOW_VIEW | ALLOW_ADD | ALLOW_EDIT | ALLOW_DELETE,
  } as IMenuEntry;
};

/**
 * Helper function for configuring menu entries.
 * @param entity - Entity for the form. Should contain a formUrl and a screen reference.
 * @param suffix - URL Suffix - Default of ':id' since the majority of modal forms are for viewing/editing the record matching the id.
 * @returns - a new menu entry
 */
const modalFieldsFromEntity = (entity: any, suffix: string = ':id'): IMenuEntry => {
  const { formUrl, operations, actions, screenReference, permissionLabel } = entity;
  return {
    url: `${formUrl}${suffix}`,
    screenReference: screenReference,
    actions: actions,
    operations: operations,
    permissionLabel: permissionLabel,
  } as IMenuEntry;
};

const webOptions: IOptions = {
  Web: [
    {
      ...workspaceFieldsFromEntity(entities.WebPage),
      icon: 'webpages',
      component: WebPageWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.WebMediaFolder),
      icon: 'webmedia',
      component: WebMediaFolderTreespace,
    },
    {
      ...workspaceFieldsFromEntity(entities.WebPromotionalCode),
      icon: 'webpromotions',
      component: WebPromotionalCodeWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.StaticBox),
      shortTitle: 'Static Blocks',
      icon: 'product',
      component: StaticBoxWorkspace,
    },
  ],
};

const companyOptions: IOptions = {
  Company: [
    {
      Configuration: [
        {
          ...workspaceFieldsFromEntity(entities.Country),
          icon: 'list',
          component: CountryWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.Currency),
          icon: 'list',
          component: CurrencyWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CompanyUser),
          icon: 'list',
          component: CompanyCompanyUserWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.FinancialCalendar),
          shortTitle: 'Financial Calendars',
          icon: 'configuration',
          component: FinancialCalendarWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ReportTemplate),
          icon: 'list',
          component: ReportTemplateWorkspace,
        },
        {
          title: 'Document Designs',
          url: '/Company/DocumentDesign',
          screenReference: 'COMPANY_DOCUMENT_DESIGN',
          icon: 'list',
          component: DocumentDesignForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.Import),
          icon: 'list',
          component: ImportWorkspace,
        },
        {
          title: 'Bulk Attachment Import',
          url: '/Company/BulkAttachmentImport',
          screenReference: 'COMPANY_BULK_ATTACHMENT_IMPORT',
          icon: 'list',
          component: BulkAttachmentImportForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportTemplate),
          icon: 'list',
          component: ImportTemplateWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.BusinessRule),
          icon: 'list',
          component: BusinessRuleWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.OptionalMandatoryFields),
          shortTitle: 'Optional Mandatory Fields',
          icon: 'list',
          component: OptionalMandatoryFieldsWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CompanyUserRole),
          title: 'User Roles',
          icon: 'list',
          component: CompanyUserRolesWorkspace,
        },
        {
          title: 'Set Order Numbers',
          url: '/Company/SetOrderNumbers',
          screenReference: 'ORDER_NUMBERS',
          icon: 'list',
          component: LastOrderNumberForm,
        },
        {
          title: 'Document Import',
          url: '/Company/Scan/ManualAttach',
          screenReference: 'ATTACH_SCANS',
          icon: 'list',
          component: ManualScanAttachForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.JobTitle),
          shortTitle: 'Contact Job Titles',
          icon: 'list',
          component: ContactJobTitleWorkspace,
          url: '/Company/JobTitle/Workspace',
        },
      ],
    },
    {
      ...formFieldsFromEntity(entities.Company),
      shortTitle: 'Configuration',
      icon: 'company',
      component: CompanyForm,
    },
    {
      ...formFieldsFromEntity(entities.StockValuationCriteria),
      shortTitle: 'Stock Valuation',
      icon: 'stockval',
      component: StockValuationForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.FinancialAudit),
      title: 'Transaction Audit',
      icon: 'companyaudit',
      component: FinancialAuditWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.DocumentSummary),
      icon: 'companydocs',
      component: DocumentSummary,
      exact: true,
    },
    {
      ...workspaceFieldsFromEntity(entities.CompanyUserNotification),
      icon: 'orderlines',
      component: UserNotificationWorkspace,
    },
    {
      title: EmailPrintPreview.title,
      url: '/Company/EmailPrintPreview',
      screenReference: EmailPrintPreview.screenReference as string,
      icon: 'template',
      component: EmailPrintPreviewForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.ReferenceAllocationFailure),
      icon: 'orderlines',
      component: ReferenceAllocationFailureWorkspace,
    },
  ],
};

const customerOptions: IOptions = {
  Customers: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.CustomerConfig),
          shortTitle: 'Configuration',
          icon: 'configuration',
          component: CustomerConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerAttributeConfig),
          shortTitle: 'Attributes',
          icon: 'list',
          component: CustomerAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerContactAttributeConfig),
          shortTitle: 'Contact Attributes',
          icon: 'list',
          component: CustomerContactAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: CustomerNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerAccountStatus),
          shortTitle: 'Account Status',
          icon: 'list',
          component: AccountStatusWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CreditAgency),
          shortTitle: 'Credit Agencies',
          icon: 'list',
          component: CreditAgencyWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerAccountManagementGroup),
          shortTitle: 'Account Management Group',
          icon: 'list',
          component: CustomerAccountManagementGroupWorkSpace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SalesPaymentTerm),
          shortTitle: 'Payment Terms',
          icon: 'list',
          component: SalesPaymentTermWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SalesCreditReason),
          shortTitle: 'Credit Reason',
          icon: 'list',
          component: CreditReasonWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.InvoiceConfiguration),
          shortTitle: 'Invoice Configuration',
          icon: 'list',
          component: InvoiceConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerBudgetProfile),
          shortTitle: 'Budget Profiles',
          icon: 'list',
          component: CustomerBudgetProfileWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.Customer),
      icon: 'customer',
      component: CustomerWorkspace,
    },
    {
      ...workspaceFieldsFromEntity({
        ...CreditControl,
      }),
      icon: 'customer',
      component: CreditControlWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.CustomerContact),
      icon: 'customer_inverted',
      component: CustomerContactWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.CustomerGlobalContact),
      icon: P4SVGIcon.CustomerGlobalContact,
      component: CustomerGlobalContactWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesInvoice),
      shortTitle: 'Invoices',
      icon: 'invoice',
      component: InvoiceWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesInvoiceLine),
      shortTitle: 'Invoice Lines',
      icon: 'invoice',
      component: InvoiceLineWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesCredit),
      shortTitle: 'Credits',
      icon: 'credit',
      component: CreditWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesCreditLine),
      shortTitle: 'Credit Lines',
      icon: 'credit',
      component: CreditLineWorkspace,
    },
    {
      ...workspaceFieldsFromEntity({
        ...entities.SalesCredit,
        title: 'Return Orders',
        workspaceUrl: '/Customers/ReturnOrders/Workspace/',
        operations: ALLOW_VIEW | ALLOW_EDIT | ALLOW_DELETE,
      }),
      shortTitle: 'Return Orders',
      icon: 'credit',
      component: ReturnOrderWorkspace,
      screenReference: 'RETURN_ORDERS',
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesReceipt),
      shortTitle: 'Receipts',
      icon: 'cashreceipt',
      component: ReceiptWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesRefund),
      shortTitle: 'Refunds',
      icon: 'cashrefund',
      component: RefundWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesAllocation),
      shortTitle: 'Allocations',
      icon: 'salesallocation',
      component: AllocationWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesOpeningBalance),
      shortTitle: 'Opening Balances',
      icon: 'openingbalance',
      component: SalesOpeningBalanceWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesBadDebt),
      shortTitle: 'Customer Bad Debt',
      icon: 'writeoff',
      component: BadDebtWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesVatOnly),
      shortTitle: 'VAT Only Invoice/Credit',
      icon: 'customervatonly',
      component: VatOnlyWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesTransaction),
      shortTitle: 'Transactions',
      icon: 'salestransactions',
      component: SalesTransactionWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.SalesTransactionEnquiry),
      shortTitle: 'Transaction Enquiry',
      icon: 'customertransenq',
      component: SalesTransactionEnquiryForm,
    },
    {
      ...formFieldsFromEntity(entities.SalesReconciliation),
      shortTitle: 'Reconciliation',
      icon: 'salesreconciliation',
      component: SalesReconciliationForm,
    },
    {
      ...formFieldsFromEntity(entities.SalesControlTotals),
      shortTitle: 'Control Totals',
      icon: 'customercontrol',
      component: SalesControlTotalsForm,
    },
    {
      url: '/Customers/SalesAnalytics',
      title: 'Analytics',
      shortTitle: 'Sales Analytics',
      icon: 'customersales',
      component: SalesAnalysis,
      screenReference: 'SALES_LEDGER_ANALYTICS',
    },
    {
      ...formFieldsFromEntity(entities.CustomerAuditByField),
      icon: 'customeraudit',
      component: CustomerAuditForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.CustomerPriceList),
      icon: 'customerpricelist',
      component: CustomerPriceListWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.CustomerPriceReview),
      icon: 'customerpricelist',
      component: CustomerPriceReviewForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.PayByLink),
      shortTitle: 'Pay By Link',
      icon: 'stocktransactions',
      component: PayByLinkWorkspace,
    },
    {
      url: '/Customers/DirectDebitGeneration',
      title: 'Direct Debit Request',
      shortTitle: 'Direct Debit Request',
      icon: 'directdebit',
      component: DirectDebitRequestGenerationForm,
      screenReference: 'DIRECT_DEBIT_REQUEST_GENERATION',
    },
    {
      AutomatedCreditControl: [
        {
          ...workspaceFieldsFromEntity(entities.AutomatedCreditControlWorkflow),
          shortTitle: 'Credit Control Workflows',
          icon: 'stocktransactions',
          component: AutomatedCreditControlWorkflowWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.AutomatedCreditControlResult),
          shortTitle: 'Credit Control Results',
          icon: 'stocktransactions',
          component: AutomatedCreditControlResultWorkspace,
          exact: true,
        },
        {
          ...workspaceFieldsFromEntity(entities.AutomatedCreditControlScheduledEmail),
          shortTitle: 'Scheduled Credit Control E-mails',
          icon: 'stocktransactions',
          component: AutomatedCreditControlScheduledEmailWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesLedgerJournal),
      shortTitle: 'Journals',
      icon: 'journal',
      component: SalesLedgerJournalWorkspace,
    },
  ],
};

const suppliersOptions: IOptions = {
  Suppliers: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.SupplierConfig),
          shortTitle: 'Configuration',
          icon: 'configuration',
          component: SupplierConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.SupplierAttributeConfig),
          shortTitle: 'Attributes',
          icon: 'list',
          component: SupplierAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SupplierContactAttributeConfig),
          shortTitle: 'Contact Attributes',
          icon: 'list',
          component: SupplierContactAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SupplierNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: SupplierNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SupplierAccountStatus),
          shortTitle: 'Account Status',
          icon: 'list',
          component: SupplierAccountStatusWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.VendorRating),
          shortTitle: 'Vendor Rating',
          icon: 'list',
          component: VendorRatingWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchasePaymentTerm),
          shortTitle: 'Payment Terms',
          icon: 'list',
          component: PurchasePaymentTermWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchasePaymentMethod),
          shortTitle: 'Payment Methods',
          icon: 'list',
          component: PurchasePaymentMethodWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.Supplier),
      icon: 'supplier',
      component: SupplierWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SupplierBankDetails),
      icon: 'supplierbank',
      component: SupplierBankDetailsWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseInvoice),
      icon: 'invoice',
      component: PurchaseInvoiceWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseCredit),
      icon: 'credit',
      component: PurchaseCreditWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchasePayment),
      shortTitle: 'Payments',
      icon: 'supplierpayment',
      component: PurchasePaymentWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseRefund),
      shortTitle: 'Refunds',
      icon: 'supplierrefund',
      component: PurchaseRefundWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SuggestedPayment),
      shortTitle: 'Suggested Payments',
      icon: 'suggestedpayments',
      component: SuggestedPaymentWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseAllocation),
      shortTitle: 'Allocations',
      icon: 'salesallocation',
      component: PurchaseAllocationWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseVatOnly),
      shortTitle: 'VAT Only Invoice/Credit',
      icon: 'customervatonly',
      component: PurchaseVatOnlyWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseOpeningBalance),
      shortTitle: 'Opening Balances',
      icon: 'openingbalance',
      component: PurchaseOpeningBalanceWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseTransaction),
      icon: 'suppliertransactions',
      component: PurchaseTransactionWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.PurchaseTransactionEnquiry),
      shortTitle: 'Transaction Enquiry',
      icon: 'customertransenq',
      component: PurchaseTransactionEnquiryForm,
    },
    {
      ...formFieldsFromEntity(entities.PurchaseReconciliation),
      shortTitle: 'Reconciliation',
      icon: 'salesreconciliation',
      component: PurchaseReconciliationForm,
    },
    {
      ...formFieldsFromEntity(entities.PurchaseControlTotals),
      shortTitle: 'Control Totals',
      icon: 'customercontrol',
      component: PurchaseControlTotalsForm,
    },
    {
      ...formFieldsFromEntity(entities.SupplierAuditByField),
      icon: 'supplieraudit',
      component: SupplierAuditForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.SupplierPriceList),
      icon: 'customerpricelist',
      component: SupplierPriceListWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.SupplierPriceReview),
      icon: 'customerpricelist',
      component: SupplierPriceReviewForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseLedgerJournal),
      shortTitle: 'Journals',
      icon: 'journal',
      component: PurchaseLedgerJournalWorkspace,
    },
  ],
};

const productOptions: IOptions = {
  Products: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.ProductConfig),
          shortTitle: 'Configuration',
          icon: 'configuration',
          component: ProductConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.ProductAttributeConfig),
          shortTitle: 'Attributes',
          icon: 'list',
          component: ProductAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ProductNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: ProductNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PhysicalDimensionDescription),
          shortTitle: 'Packing Descriptions',
          icon: 'list',
          component: PhysicalDimensionDescriptionWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ProductCategoryAttributeConfig),
          shortTitle: 'Product Category Attributes',
          icon: 'list',
          component: ProductCategoryAttributeConfigWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.DocumentConfig),
          title: 'Product Document Configuration',
          shortTitle: entities.DocumentConfig.formTitle,
          url: entities.DocumentConfig.formUrlProduct,
          screenReference: entities.DocumentConfig.screenReferenceProduct,
          icon: 'configuration',
          component: ProductDocumentConfigForm,
          operations: entities.DocumentConfig.operations,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.Product),
      icon: 'product',
      component: ProductWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.ProductCategory),
      icon: 'productcategory',
      component: ProductCategoryTreespace,
    },
    {
      ...workspaceFieldsFromEntity(entities.ProductTemplate),
      icon: 'template',
      component: ProductTemplateWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.ProductAudit),
      icon: 'productaudit',
      component: ProductAuditForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.ProductsStockTransaction),
      icon: 'stocktransactions',
      component: StockTransactionWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.Certificate),
      icon: 'certificate',
      component: ProductsCertificateWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.ProductUsage),
      icon: 'usage',
      component: ProductUsageWorkspace,
    },
    {
      title: 'Traceability',
      url: '/Products/Traceability',
      screenReference: 'PRODUCT_TRACEABILITY',
      icon: 'customertransenq',
      component: TraceabilityForm,
    },
    {
      ...formFieldsFromEntity(entities.DeadStock),
      icon: 'deadstock',
      component: DeadStockPrintForm,
      operations: ALLOW_EDIT,
    },
    {
      ...formFieldsFromEntity(entities.ProductMinMax),
      icon: 'minmax',
      component: MinMaxLevelsForm,
    },
    {
      ...formFieldsFromEntity(entities.ProductBulkImageImport),
      icon: 'imageimp',
      component: ProductBulkImageImportForm,
    },
    {
      title: 'Product Supplier Details',
      icon: 'supplier',
      url: '/Products/ProductSupplierDetails/Workspace/',
      component: ProductSupplierDetailsWorkspace,
      screenReference: 'PRODUCT_SUPPLIER_DETAILS',
    },
    {
      EPR: [
        {
          Configuration: [],
        },
        {
          ...workspaceFieldsFromEntity(entities.Material),
          icon: 'product',
          component: MaterialWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ProductMaterialDetail),
          icon: 'product',
          component: ProductMaterialDetailWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SubMaterial),
          icon: 'product',
          component: SubMaterialWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.MaterialColour),
          icon: 'product',
          component: MaterialColourWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.EprAggregateRecord),
          icon: 'product',
          component: EprAggregateRecordWorkspace,
        },
      ],
    },
    {
      StockControl: [
        {
          Configuration: [
            {
              ...workspaceFieldsFromEntity(entities.LotAttributeConfig),
              icon: 'list',
              component: LotAttributeConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.StockAdjustmentReason),
              icon: 'list',
              component: StockAdjustmentReasonWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.StockTransferReason),
              icon: 'list',
              component: StockTransferReasonWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.ConsolidatedPickGroup),
              icon: 'list',
              component: ConsolidatedPickGroupWorkspace,
            },
          ],
        },
        {
          ...workspaceFieldsFromEntity(entities.Warehouse),
          shortTitle: 'Warehouses',
          url: '/Products/StockControl/Warehouse/Workspace',
          icon: 'warehouse',
          component: WarehouseWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.BinLocation),
          icon: 'binlocations',
          component: BinLocationWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.GoodsReceipt),
          icon: 'goodsinward',
          component: GoodsReceiptWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.GoodsReceiptLine),
          icon: 'goodsinreport',
          component: GoodsReceiptLineWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockAdjustment),
          icon: 'stockadjustment',
          component: StockAdjustmentWorkspace,
        },
        {
          title: 'Stock Adjustment Labels',
          url: '/Products/StockAdjustmentLabels/Workspace',
          screenReference: 'STOCK_ADJUSTMENT_LABEL',
          icon: P4SVGIcon.StockAdjustmentPrintWorkspace,
          component: StockAdjustmentLabelDocumentPrintForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockTransfer),
          icon: 'stocktransfer',
          component: StockTransferWorkspace,
        },
        {
          title: `${entities.StockTake.formTitle} Direct`,
          url: `${entities.StockTake.formUrl}Direct`,
          screenReference: `${entities.StockTake.screenReference}_DIRECT`,
          icon: 'stocktakedirect',
          component: ProductsStockTakeDirectForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockTake),
          title: `${entities.StockTake.formTitle} Progressive`,
          screenReference: `${entities.StockTake.screenReference}_PROGRESSIVE`,
          icon: 'stocktakeprog',
          component: ProductsStockTakeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockTake),
          title: `Legacy ${entities.StockTake.formTitle} Progressive`,
          screenReference: `PRODUCTS_STOCK_CONTROL_LEGACY_STOCK_TAKE_PROGRESSIVE`,
          icon: 'stocktakeprog',
          component: LegacyProductsStockTakeWorkspace,
          url: `${entities.StockTake.oldWorkspaceUrl}`,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockTakeStatus),
          icon: 'stocktakeprog',
          component: StockTakeStatusWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockError),
          title: 'WMS Picking Errors',
          icon: 'deadstock',
          url: '/Products/StockControl/StockError/Workspace/',
          component: StockErrorWorkspace,
          screenReference: 'STOCK_ERROR',
        },
        {
          title: 'Receipt Labels',
          url: '/Products/ReceiptLabels/Workspace',
          screenReference: 'PRODUCT_RECEIPT_LABELS',
          icon: 'receiptlabel',
          component: ReceiptLabelDocumentPrintForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.StockLot),
          icon: 'bincontents',
          component: ProductsStockLotWorkspace,
        },
        {
          title: 'Product Warehouse Details',
          icon: 'warehouse_detail',
          url: '/Products/StockControl/ProductWarehouseDetails/Workspace/',
          component: ProductWarehouseDetailsWorkspace,
          screenReference: 'PRODUCT_WAREHOUSE_DETAILS',
        },
        {
          ...workspaceFieldsFromEntity(entities.BinReplenishmentHeader),
          icon: 'goodsinward',
          component: BinReplenishmentHeaderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.BinReplenishmentDetail, ':jobid/:header'),
          icon: 'goodsinward',
          component: BinReplenishmentDetailWorkspace,
        },
      ],
    },
    {
      Edata: [
        {
          Configuration: [
            {
              ...formFieldsFromEntity(entities.EdataConfig),
              shortTitle: 'Configuration',
              icon: 'configuration',
              component: EdataConfigForm,
            },
          ],
        },
        {
          title: 'Edata Enquiry',
          shortTitle: 'Edata Enquiry',
          url: `${entities.EdataProduct.enquiryUrl}`,
          screenReference: 'EDATA_ENQUIRY',
          operations: ALLOW_VIEW,
          icon: 'customertransenq',
          component: EdataEnquiryView,
        },
      ],
    },
    {
      Luckins: [
        {
          Configuration: [
            {
              ...formFieldsFromEntity(entities.LuckinsConfig),
              shortTitle: 'Configuration',
              icon: 'configuration',
              component: LuckinsConfigForm,
            },
          ],
        },
        {
          title: 'Luckins Enquiry',
          shortTitle: 'Luckins Enquiry',
          url: `${entities.LuckinsItem.enquiryUrl}`,
          screenReference: 'LUCKINS_ENQUIRY',
          operations: ALLOW_VIEW,
          icon: 'customertransenq',
          component: LuckinsEnquiryView,
        },
        {
          ...workspaceFieldsFromEntity(entities.LuckinsItem),
          icon: 'product',
          component: LuckinsItemWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.LuckinsSupplier),
          icon: 'supplier',
          component: LuckinsSupplierWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.LuckinsVatRate),
          icon: 'vatrate',
          component: LuckinsVatRateWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.LuckinsImport),
          icon: 'productimport',
          component: LuckinsImportWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.LuckinsItemImportFailure),
          icon: 'orderlines',
          component: LuckinsItemImportFailureWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.ProductPriceMatrix),
      icon: 'productpricemat',
      component: ProductPriceMatrixWorkspace,
      ignoreInSearch: true, //The 'home' location is Sales/CustomerPricing/ProductPriceMatrix - Don't display twice in search bar.
    },
    {
      ...workspaceFieldsFromEntity(entities.PromotionalMessage),
      icon: 'promomessage',
      component: PromotionalMessageWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.AutomaticDispatchResult),
      icon: 'product',
      component: AutomaticDispatchResultWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.Allocation),
      icon: 'product',
      component: ProductAllocationWorkspace,
    },
    {
      Assembly: [
        {
          Configuration: [
            {
              ...workspaceFieldsFromEntity(entities.AssemblyAttributeConfig),
              shortTitle: 'Assembly Attributes',
              icon: 'list',
              component: AssemblyAttributeConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.AssemblyNoteType),
              shortTitle: 'Note Types',
              icon: 'list',
              component: AssemblyNoteTypeWorkspace,
            },
          ],
        },
        {
          ...workspaceFieldsFromEntity(entities.Assembly),
          icon: 'product',
          component: AssemblyWorkspace,
          beta: true,
        },
        {
          ...workspaceFieldsFromEntity(entities.AssemblyRequirementDetail),
          icon: 'shortages',
          component: AssemblyRequirementDetailWorkspace,
          beta: true,
        },
      ],
    },
  ],
};

const salesOptions: IOptions = {
  Sales: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.SalesOrderConfig),
          shortTitle: 'Order Configuration',
          icon: 'configuration',
          component: OrderConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.SalesNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: SalesNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SalesAttributeConfig),
          icon: 'list',
          component: SalesAttributeConfigWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.DocumentConfig),
          title: 'Sales Document Configuration',
          shortTitle: entities.DocumentConfig.formTitle,
          url: entities.DocumentConfig.formUrlSales,
          screenReference: entities.DocumentConfig.screenReferenceSales,
          icon: 'configuration',
          component: SalesDocumentConfigForm,
          operations: entities.DocumentConfig.operations,
        },
        {
          ...workspaceFieldsFromEntity(entities.Carrier),
          icon: 'configuration',
          component: Carriers,
        },
        {
          ...workspaceFieldsFromEntity(entities.Haulier),
          icon: 'configuration',
          component: Hauliers,
        },
        {
          ...workspaceFieldsFromEntity(entities.PacTerminal),
          icon: 'configuration',
          component: PacTerminals,
        },
        {
          ...workspaceFieldsFromEntity(entities.DeliveryMethod),
          icon: 'configuration',
          component: DeliveryMethods,
        },
        {
          ...workspaceFieldsFromEntity(entities.DeliveryRoute),
          icon: 'configuration',
          component: DeliveryRoutes,
        },
        {
          ...workspaceFieldsFromEntity(entities.PaymentMethod),
          icon: 'configuration',
          component: PaymentMethod,
        },
        {
          ...workspaceFieldsFromEntity(entities.TradeCounterTerminal),
          icon: 'configuration',
          component: TradeCounterTerminalWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.MaxoptraConfiguration),
          icon: 'configuration',
          component: MaxoptraConfigurationForm,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesOrder),
      icon: 'order',
      component: SalesOrderWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.Quotation),
      icon: 'quote',
      component: QuotationWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.CombinedSales),
      icon: 'orderquote',
      component: CombinedSalesWorkspace,
    },
    {
      url: '/Sales/TradeCounter/Details/New',
      title: 'Trade Counter',
      shortTitle: 'Trade Counter',
      icon: 'tradecounter',
      screenReference: 'TRADE_COUNTER',
      component: TradeCounterForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.PaymentLine),
      icon: 'suppliertransactions',
      component: PaymentLineWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.TradeCounterReconcile),
      icon: 'tradecounter',
      component: TradeCounterReconcileWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.SalesOrderAuditByField),
      icon: 'orderaudit',
      component: SalesOrderAuditForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.SalesOrderLine),
      title: 'Quote & Order Lines',
      icon: 'orderlines',
      component: SalesOrderLineWorkspace,
      screenReference: 'QUOTE_ORDER_LINES',
    },
    {
      ...formFieldsFromEntity(entities.Acknowledgement),
      icon: 'printack',
      component: AcknowledgementPrintForm,
    },
    {
      ...formFieldsFromEntity(entities.QuotationDocument),
      icon: 'printquote',
      component: QuotationDocumentPrintForm,
    },
    {
      ...formFieldsFromEntity(entities.ProformaInvoice),
      icon: 'printproforma',
      component: ProformaInvoiceForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.DispatchInstructions),
      icon: 'printdn',
      component: DispatchInstructionsWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.DispatchInstructionLine),
      icon: 'printdn',
      component: DispatchInstructionLinesWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.IntrastatDispatchInstructionLine),
      icon: 'printdn',
      component: IntrastatDispatchInstructionLinesWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.DataSheet),
      icon: 'printdatasheet',
      component: DataSheetForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.ConnectTransaction),
      shortTitle: 'Connect PAC Transactions',
      icon: 'stocktransactions',
      component: ConnectTransactionWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(SalesOrderDeliveryAddress),
      icon: P4SVGIcon.SalesOrderDeliveryAddress,
      component: SalesOrderDeliveryAddressWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.Allocation),
      icon: 'product',
      component: ProductAllocationWorkspace,
    },
    {
      CustomerPricing: [
        {
          Configuration: [
            {
              ...formFieldsFromEntity(entities.PricingConfig),
              icon: 'configuration',
              component: PricingConfigForm,
            },
            {
              ...workspaceFieldsFromEntity(entities.CustomerPriceSource),
              icon: 'list',
              component: CustomerPriceSourceWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.CustomerContractReference),
              shortTitle: 'Customer Contract References',
              icon: 'list',
              component: CustomerContractReferenceWorkspace,
            },
          ],
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerSpecialPrices),
          icon: 'customerspecialprice',
          component: SpecialPricesWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PromotionalPrice),

          icon: 'promotionalprice',
          component: PromotionalPriceWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ProductPriceMatrix),
          icon: 'productpricemat',
          component: ProductPriceMatrixWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerPriceMatrix),
          icon: 'customerpricematrix',
          component: CustomerPriceMatrixWorkspace,
        },
      ],
    },
    {
      Channel: [
        {
          Configuration: [
            {
              ...workspaceFieldsFromEntity(entities.EbayConfig),
              icon: 'configuration',
              component: EbayConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.AmazonConfig),
              icon: 'configuration',
              component: AmazonConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.ShopifyConfiguration),
              icon: 'configuration',
              component: ShopifyConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.PixSellConfiguration),
              icon: 'configuration',
              component: PixSellConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.WooCommerceConfiguration),
              icon: 'configuration',
              component: WooCommerceConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.EtsyConfiguration),
              icon: 'configuration',
              component: EtsyConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.BigCommerceConfiguration),
              icon: 'configuration',
              component: BigCommerceConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.MagentoConfiguration),
              icon: 'configuration',
              component: MagentoConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.WebConfiguration),
              icon: 'configuration',
              component: WebConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.EdiConfiguration),
              icon: 'configuration',
              component: EdiConfigWorkspace,
            },
            {
              ...workspaceFieldsFromEntity(entities.ChannelConfig),
              icon: 'configuration',
              component: ChannelConfigWorkspace,
            },
          ],
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedAmazonOrder),
          icon: 'amazonorders',
          component: ImportedAmazonOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.AmazonSettlementReportItem),
          icon: 'amazonorders',
          component: AmazonSettlementReportItemWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedEbayOrder),
          icon: 'ebayorders',
          component: ImportedEbayOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedShopifyOrder),
          icon: 'order',
          component: ImportedShopifyOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedPixSellOrder),
          icon: 'order',
          component: ImportedPixSellOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedWooCommerceOrder),
          icon: 'order',
          component: ImportedWooCommerceOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedEtsyOrder),
          icon: 'order',
          component: ImportedEtsyOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedBigCommerceOrder),
          icon: 'order',
          component: ImportedBigCommerceOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedMagentoOrder),
          icon: 'order',
          component: ImportedMagentoOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedEdiOrder),
          icon: 'order',
          component: ImportedXediOrderWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.ImportedWebOrder),
          icon: 'order',
          component: ImportedWebOrderWorkspace,
        },
      ],
    },
    {
      Picking: [
        {
          ...formFieldsFromEntity(entities.PickingInstruction),
          icon: 'printpicking',
          component: PickingInstructionPrintForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.ConsolidatedPick),
          icon: 'product',
          component: ConsolidatedPickWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PickingBox),
          shortTitle: 'Picking Boxes',
          icon: 'product',
          component: PickingBoxWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PickingBoxLine),
          shortTitle: 'Picking Box Lines',
          icon: 'product',
          component: PickingBoxLineWorkspace,
        },
        {
          Configuration: [
            {
              ...workspaceFieldsFromEntity(entities.Tote),
              shortTitle: 'Totes',
              icon: 'product',
              component: ToteWorkspace,
            },
          ],
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.CarriageCost),
      icon: 'carriagecosts',
      component: CarriageCostsWorkspace,
    },
  ],
};

const diaryOptions: IOptions = {
  Diary: [
    {
      Configuration: [
        {
          ...workspaceFieldsFromEntity(entities.EventType),
          icon: 'list',
          component: TypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.DiaryResult),
          icon: 'list',
          component: ResultWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.DiaryAttributeConfig),
          icon: 'list',
          component: AttributeWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.DiaryEvent),
      icon: 'event',
      component: EventWorkspace,
    },
  ],
};

const purchaseOptions: IOptions = {
  Purchasing: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.PurchaseConfig),
          icon: 'configuration',
          component: PurchaseConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchaseNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: PurchaseNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchaseAttributeConfig),
          icon: 'list',
          component: PurchaseAttributeConfigWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchaseEdiConfiguration),
          shortTitle: 'Edi Configuration',
          icon: 'configuration',
          component: PurchaseEdiConfigWorkspace,
        },
        {
          title: 'Purchasing Document Configuration',
          shortTitle: entities.DocumentConfig.formTitle,
          url: entities.DocumentConfig.formUrlPurchasing,
          screenReference: entities.DocumentConfig.screenReferencePurchasing,
          icon: 'configuration',
          component: PurchaseDocumentConfigForm,
          operations: entities.DocumentConfig.operations,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseOrder),
      icon: 'purchaseorder',
      component: PurchaseOrderWorkspace,
      exact: true,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseOrderLine),
      icon: 'polines',
      component: PurchaseOrderLineWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseCost),
      icon: 'salesreconciliation',
      component: PurchaseCostWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.PurchaseOrderDocument),
      icon: 'printpo',
      component: PurchaseOrderDocumentPrintForm,
    },
    {
      ...formFieldsFromEntity(DebitNoteDocument),
      icon: 'printpo',
      component: DebitNoteDocumentPrintForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.PurchaseRequirement),
      icon: 'shortages',
      component: PurchaseRequirementWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.IntrastatGoodsReceiptLine),
      icon: 'printdn',
      component: IntrastatGoodsReceiptLinesWorkspace,
    },
    {
      SupplierPricing: [
        {
          ...workspaceFieldsFromEntity(entities.SupplierPriceMatrix),
          icon: 'supplierpricing',
          component: SupplierPriceMatrixWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.SupplierReturn),
      icon: 'purchaseorder',
      component: SupplierReturnWorkspace,
    },
  ],
};

const nominalOptions: IOptions = {
  Nominal: [
    {
      Configuration: [
        {
          ...workspaceFieldsFromEntity(entities.Dimension),
          shortTitle: 'Dimensions',
          icon: 'list',
          component: DimensionWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalBusinessCategory),
          shortTitle: 'Business Categories',
          icon: 'list',
          component: NominalBusinessCategoryWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalProductCategory),
          shortTitle: 'Product Categories',
          icon: 'list',
          component: NominalProductCategoryWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalReportCategory),
          shortTitle: 'Report Categories',
          icon: 'list',
          component: ReportCategoryWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalCommonAccountSet),
          icon: 'list',
          component: CommonAccountSetWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalCustomerAccountSet),
          icon: 'list',
          component: CustomerAccountSetWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalSupplierAccountSet),
          icon: 'list',
          component: SupplierAccountSetWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalProductAccountSet),
          icon: 'list',
          component: ProductAccountSetWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(NominalWarehouseAccountSet),
          icon: 'list',
          component: NominalWarehouseAccountSetWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.NominalConfig),
          shortTitle: 'Configuration',
          icon: 'configuration',
          component: NominalConfigForm,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalNoteType),
          shortTitle: 'Note Types',
          icon: 'list',
          component: NominalNoteTypeWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalBudgetProfile),
          shortTitle: 'Budget Profiles',
          icon: 'list',
          component: NominalBudgetProfileWorkspace,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.Nominal),
      shortTitle: 'Accounts',
      icon: 'nominalaccount',
      component: NominalWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.NominalTransaction),
      shortTitle: 'Transactions',
      icon: 'nominaltransactions',
      component: NominalTransactionWorkspace,
      exact: true,
    },
    {
      url: '/Nominal/NominalAnalytics',
      title: 'Nominal Analytics',
      shortTitle: 'Analytics',
      icon: 'nominalanalytics',
      component: NominalAnalysis,
      screenReference: 'NOMINAL_ANALYTICS',
    },
    {
      ...workspaceFieldsFromEntity(entities.NominalTemplate),
      shortTitle: 'Templates',
      icon: 'template',
      component: TemplateWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.NominalJournal),
      shortTitle: 'Journals',
      icon: 'journal',
      component: JournalWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.NominalAccrualPrepayment),
      shortTitle: 'Accruals & Prepayments',
      icon: 'accrual',
      component: AccrualPrepaymentWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.NominalCostOfSale),
      shortTitle: 'Cost Of Sale',
      icon: 'nominalcost',
      component: NominalCostOfSaleForm,
    },
    {
      ...formFieldsFromEntity(entities.NominalAuditByField),
      icon: 'nominalaudit',
      component: NominalAuditForm,
    },
    {
      title: 'Nominal Year End',
      url: '/Nominal/YearEnd/Form',
      icon: 'yearend',
      screenReference: 'NOM_YEAREND',
      component: NominalYearEnd,
    },
    {
      FinancialReports: [
        {
          ...formFieldsFromEntity(entities.NominalTrialBalance),
          icon: 'openingbalance',
          component: NominalTrialBalance,
        },
        {
          ...formFieldsFromEntity(entities.NominalProfitLoss),
          icon: 'openingbalance',
          component: NominalProfitLoss,
        },
        {
          ...formFieldsFromEntity(entities.NominalBalanceSheet),
          icon: 'openingbalance',
          component: NominalBalanceSheet,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalReport),
          shortTitle: 'Report Layouts',
          icon: 'list',
          component: ReportWorkspace,
        },
      ],
    },
  ],
};

const vatOptions: IOptions = {
  Vat: [
    {
      Configuration: [
        {
          ...workspaceFieldsFromEntity(entities.VatAuthority),
          shortTitle: 'Authorities',
          icon: 'vatauthority',
          component: AuthorityWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.VatBusinessCategory),
          shortTitle: 'Business VAT Status',
          icon: 'list',
          component: VatBusinessCategoryWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.VatProductCategory),
          shortTitle: 'Product VAT Category',
          icon: 'list',
          component: VatProductCategoryWorkspace,
        },
        {
          ...formFieldsFromEntity(entities.VatSwitch),
          shortTitle: 'Switch VAT Scheme',
          icon: 'list',
          component: SwitchForm,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.VatRate),
      shortTitle: 'Rates',
      icon: 'vatrate',
      component: RateWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.VatTransaction),
      shortTitle: 'Transactions',
      icon: 'vattransactions',
      component: VatTransactionWorkspace,
    },
    {
      ...formFieldsFromEntity(entities.VatReconciliation),
      icon: 'vatrec',
      component: ReconciliationForm,
    },
    {
      url: '/Vat/RateTotals',
      title: 'VAT Analysis',
      shortTitle: 'Analysis',
      icon: 'vatanalysis',
      screenReference: 'VAT_TOTALS',
      component: RateTotalsForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.VatReturn),
      shortTitle: 'Return',
      icon: 'vatreturn',
      component: ReturnWorkspace,
    },
    {
      url: '/Vat/EcSalesListing/Print',
      title: 'EC Sales Listing',
      shortTitle: 'EC Sales Listing',
      icon: 'vattransactions',
      component: EcSalesListingForm,
      screenReference: 'EC_SALES_LISTING_PRINT',
    },
  ],
};

const bankOptions: IOptions = {
  Bank: [
    {
      Configuration: [
        {
          ...formFieldsFromEntity(entities.BankConfig),
          shortTitle: 'Configuration',
          icon: 'configuration',
          component: BankConfigForm,
        },
      ],
    },
    {
      ...workspaceFieldsFromEntity(entities.Bank),
      icon: 'bank',
      component: BankWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.BankPaymentReceipt),
      shortTitle: 'Payments/Receipts',
      icon: 'bankreceipt',
      component: BankPaymentReceiptWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.BankTransfer),
      icon: 'banktransfer',
      component: BankTransferWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.BankReconciliation),
      icon: 'bankrec',
      component: BankReconciliationWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.BankRevaluation),
      icon: 'bankrevalue',
      component: BankRevaluationWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.Bank, 'BankFeed'),
      url: '/Bank/BankFeed/Workspace',
      title: 'Bank Feeds',
      shortTitle: 'Bank Feeds',
      icon: 'bank',
      beta: true,
      component: BankFeedWorkspace,
      screenReference: 'BANK_FEED',
    },
  ],
};

const reportOptions: IOptions = {
  Reports: [
    {
      Financial: [
        {
          ...formFieldsFromEntity(entities.NominalTrialBalance),
          title: 'Trial Balance',
          report: true,
          component: NominalTrialBalance,
        },
        {
          ...formFieldsFromEntity(entities.NominalProfitLoss),
          icon: 'Profit and Loss',
          report: true,
          component: NominalProfitLoss,
        },
        {
          ...formFieldsFromEntity(entities.NominalBalanceSheet),
          icon: 'Balance Sheet',
          report: true,
          component: NominalBalanceSheet,
        },
        {
          ...workspaceFieldsFromEntity(entities.Nominal),
          title: 'Account Transactions',
          report: true,
          component: NominalWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.NominalReport),
          title: 'Report Layouts',
          report: true,
          component: ReportWorkspace,
        },
      ],
      Customers: [
        {
          ...workspaceFieldsFromEntity(entities.Customer),
          title: 'Aged Debtors Summary/Detail',
          report: true,
          component: CustomerWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.SalesTransaction),
          title: 'Sales Daybook',
          report: true,
          component: SalesTransactionWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.Customer),
          title: 'Customer Statements',
          report: true,
          component: CustomerWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerContact),
          title: 'Customer Mailshot',
          report: true,
          component: CustomerContactWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.CustomerContact),
          title: 'Customer Labels',
          report: true,
          component: CustomerContactWorkspace,
        },
      ],
      Suppliers: [
        {
          ...workspaceFieldsFromEntity(entities.Supplier),
          title: 'Aged Creditors Detail/Summary',
          report: true,
          component: SupplierWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.PurchaseTransaction),
          title: 'Purchase Daybook',
          report: true,
          component: PurchaseTransactionWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.Supplier),
          title: 'Suggested Payments',
          report: true,
          component: SupplierWorkspace,
        },
      ],
      Vat: [
        {
          ...workspaceFieldsFromEntity(entities.VatReturn),
          title: 'VAT Return',
          report: true,
          component: ReturnWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.VatTransaction),
          title: 'VAT Audit',
          report: true,
          component: VatTransactionWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.VatTransaction),
          title: 'VAT Exception',
          report: true,
          component: VatTransactionWorkspace,
        },
        {
          url: '/Vat/EcSalesListing/Print',
          title: 'EC Sales Listing',
          shortTitle: 'EC Sales Listing',
          component: EcSalesListingForm,
          screenReference: 'EC_SALES_LISTING_PRINT',
        },
        {
          ...workspaceFieldsFromEntity(entities.IntrastatDispatchInstructionLine),
          title: 'Intrastat Dispatch Report',
          report: true,
          component: IntrastatDispatchInstructionLinesWorkspace,
        },
        {
          ...workspaceFieldsFromEntity(entities.IntrastatGoodsReceiptLine),
          title: 'Intrastat Arrivals Report',
          report: true,
          component: IntrastatGoodsReceiptLinesWorkspace,
        },
      ],
      Products: [
        {
          ...formFieldsFromEntity(entities.StockValuationCriteria),
          title: 'Stock Valuation',
          report: true,
          component: StockValuationForm,
        },
        {
          ...formFieldsFromEntity(entities.DeadStock),
          title: 'Dead Stock',
          report: true,
          excludeSearch: true,
          component: DeadStockPrintForm,
        },
      ],
    },
  ],
};

const systemOptions: IOptions = {
  System: [
    {
      ...formFieldsFromEntity(entities.SystemLicense),
      icon: 'supplierpayment',
      component: SystemLicenseForm,
    },
    {
      ...formFieldsFromEntity(entities.SystemConfig),
      icon: 'configuration',
      component: SystemConfigForm,
    },
    {
      ...workspaceFieldsFromEntity(entities.SmtpConfig),
      icon: 'promomessage',
      component: SmtpConfigWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SystemSmtpLog),
      icon: 'orderlines',
      component: SmtpLogWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SystemCompany),
      icon: 'companymodule',
      component: CompanyWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SystemUser),
      icon: 'customermodule',
      component: SystemUserWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.SystemPrinter),
      icon: 'printer',
      component: SystemPrinterWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.LoggedInUser),
      icon: 'suppliermodule',
      component: LoggedInUserWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.JobInfo),
      icon: 'orderlines',
      component: JobsWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.JobReport),
      icon: 'companyaudit',
      component: JobReportWorkspace,
    },
    {
      ...workspaceFieldsFromEntity(entities.RegisteredDevice),
      icon: 'bankfeedrule',
      component: RegisteredDevicesWorkspace,
    },
  ],
};

export const allOptions: IOptions = {
  ...companyOptions,
  ...customerOptions,
  ...suppliersOptions,
  ...productOptions,
  ...salesOptions,
  ...diaryOptions,
  ...purchaseOptions,
  ...nominalOptions,
  ...vatOptions,
  ...bankOptions,
  ...webOptions,
  ...reportOptions,
  ...systemOptions,
};

export const modalRoutes: Array<IMenuEntry> = [
  {
    url: '/Company/ReportTemplateFields/:reportType',
    component: CompanyReportTemplateFieldForm,
    exact: true,
    screenReference: 'DOCUMENT_TEMPLATE_FIELDS',
    parent: 'COMPANY',
  },
  {
    url: '/SetupComplete',
    component: SetupComplete,
  },
  {
    ...modalFieldsFromEntity(entities.Country),
    component: CountryForm,
  },
  {
    ...modalFieldsFromEntity(entities.Currency),
    component: CurrencyForm,
  },
  {
    url: '/Sales/TradeCounter/Details/:id',
    component: TradeCounterForm,
    screenReference: 'TRADE_COUNTER',
    parent: 'SALES',
  },
  {
    ...modalFieldsFromEntity(entities.Import),
    component: ImportForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportTemplate),
    component: ImportTemplateForm,
  },
  {
    ...modalFieldsFromEntity(entities.CompanyUser),
    component: UserForm,
  },
  {
    ...modalFieldsFromEntity(entities.FinancialCalendar),
    component: FinancialCalendarForm,
  },
  {
    ...modalFieldsFromEntity(entities.CompanyUserRole),
    component: CompanyUserRoleForm,
  },
  {
    ...modalFieldsFromEntity(entities.ReportTemplate),
    component: ReportTemplateForm,
  },
  {
    url: '/Company/DocumentTemplateFields/:reportType',
    component: ReportTemplateFieldForm,
    screenReference: 'DOCUMENT_TEMPLATE_FIELDS',
    parent: 'COMPANY',
  },
  {
    ...modalFieldsFromEntity(entities.CustomerAttributeConfig),
    component: CustomerAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerAccountManagementGroup),
    component: CustomerAccountManagementGroupForm,
  },
  {
    ...modalFieldsFromEntity(entities.AutomatedCreditControlWorkflow),
    component: AutomatedCreditControlWorkflowForm,
  },
  {
    ...modalFieldsFromEntity(entities.StaticBox),
    component: StaticBoxForm,
  },
  {
    ...modalFieldsFromEntity(entities.PickingBox),
    component: PickingBoxForm,
  },
  {
    ...modalFieldsFromEntity(entities.Tote, ':id'),
    component: ToteForm,
  },
  {
    ...modalFieldsFromEntity(entities.Material),
    component: MaterialForm,
  },
  {
    ...modalFieldsFromEntity(entities.SubMaterial),
    component: SubMaterialForm,
  },
  {
    ...modalFieldsFromEntity(entities.MaterialColour),
    component: MaterialColourForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerContactAttributeConfig),
    component: CustomerContactAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.Customer),
    component: CustomerForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerBudgetProfile),
    component: CustomerBudgetProfileForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerNoteType),
    component: CustomerNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseNoteType),
    component: PurchaseNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseEdiConfiguration),
    component: PurchaseEdiConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseCost),
    component: PurchaseCostForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrderReceipt),
    component: PurchaseOrderReceiptForm,
    parent: 'PURCHASING_ORDERS',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrderCancelReceipt),
    component: PurchaseOrderCancelReceiptForm,
    parent: 'PURCHASING_ORDERS',
  },
  {
    ...modalFieldsFromEntity(entities.CustomerSpecialPrices),
    component: SpecialPricesForm,
  },
  {
    ...modalFieldsFromEntity(entities.PromotionalPrice),
    component: PromotionalPriceForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerPriceSource),
    component: CustomerPriceSourceForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerGlobalAmend),
    component: CustomerGlobalAmendForm,
    parent: 'CUSTOMERS',
  },
  {
    ...modalFieldsFromEntity(entities.CustomerAccountStatus),
    component: AccountStatusForm,
  },
  {
    ...modalFieldsFromEntity(entities.CreditAgency),
    component: CreditAgencyForm,
  },
  {
    ...modalFieldsFromEntity(entities.ProductAttributeConfig),
    component: ProductAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.ProductCategoryAttributeConfig),
    component: ProductCategoryAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.AssemblyAttributeConfig),
    component: AssemblyAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.ProductNoteType),
    component: ProductNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.AssemblyNoteType),
    component: AssemblyNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.PhysicalDimensionDescription),
    component: PhysicalDimensionDescriptionForm,
  },
  {
    ...modalFieldsFromEntity(entities.LotAttributeConfig),
    component: LotAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.StockAdjustmentReason),
    component: StockAdjustmentReasonForm,
  },
  {
    ...modalFieldsFromEntity(entities.StockTransferReason),
    component: StockTransferReasonForm,
  },
  {
    ...modalFieldsFromEntity(entities.Warehouse),
    component: WarehouseForm,
  },
  {
    ...modalFieldsFromEntity(entities.BinLocation),
    component: BinLocationForm,
  },
  {
    ...modalFieldsFromEntity(entities.BinLocation),
    url: '/Products/StockControl/BinLocation/RangeDetails', //Override URL from entity.
    component: BinLocationRangeForm,
    parent: 'STOCKCONTROL',
  },
  {
    ...modalFieldsFromEntity(entities.StockLot),
    component: StockLotForm,
    parent: 'STOCKCONTROL',
  },
  {
    ...modalFieldsFromEntity(entities.GoodsReceipt),
    component: GoodsReceiptForm,
  },
  {
    ...modalFieldsFromEntity(entities.StockAdjustment, ':id/:kitOpt?'),
    component: StockAdjustmentForm,
  },
  {
    ...modalFieldsFromEntity(entities.StockTransfer),
    component: StockTransferForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.StockTransfer, ':jobid'),
    component: StockTransferWorkspace,
  },
  {
    ...modalFieldsFromEntity(entities.Product),
    component: ProductForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.ProductTemplate),
    component: ProductForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(LuckinsItem),
    component: LuckinsItemForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(EdataProduct),
    component: EdataProductForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.ProductGlobalAmend),
    component: ProductGlobalAmendForm,
    parent: 'PRODUCTS',
  },
  {
    ...modalFieldsFromEntity(entities.ProductAllocation),
    component: ProductAllocationForm,
  },
  {
    ...modalFieldsFromEntity(entities.Certificate),
    component: ProductsCertificateForm,
    parent: 'PRODUCTS',
  },
  {
    ...modalFieldsFromEntity(entities.ProductCategory),
    component: ProductCategoryForm,
    parent: 'PRODUCTS',
  },
  {
    ...modalFieldsFromEntity(entities.ProductPriceMatrix),
    component: ProductPriceMatrixForm,
  },
  {
    ...modalFieldsFromEntity(entities.PromotionalMessage),
    component: PromotionalMessageForm,
  },
  {
    ...modalFieldsFromEntity(entities.LuckinsSupplier),
    component: LuckinsSupplierForm,
  },
  {
    ...modalFieldsFromEntity(entities.LuckinsVatRate),
    component: LuckinsVatRateForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerPriceMatrix),
    component: CustomerPriceMatrixForm,
    parent: 'CUSTOMERPRICING',
  },
  {
    ...modalFieldsFromEntity(entities.SupplierPriceMatrix),
    component: SupplierPriceMatrixForm,
    parent: 'SUPPLIERPRICING',
  },
  {
    ...modalFieldsFromEntity(entities.SupplierNoteType),
    component: SupplierNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.Supplier),
    component: SupplierForm,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierBankDetails),
    component: SupplierBankDetailsForm,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierAttributeConfig),
    component: SupplierAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierContactAttributeConfig),
    component: SupplierContactAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierGlobalAmend),
    component: SupplierGlobalAmendForm,
    parent: 'SUPPLIERS',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseInvoice),
    component: PurchaseInvoiceForm,
  },
  {
    ...modalFieldsFromEntity(entities.MatchPurchaseOrders),
    component: MatchPurchaseOrdersForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.MatchPurchaseOrders, 'FromPO/:id'),
    component: MatchPurchaseOrdersForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseInvoiceQuick),
    component: QuickPurchaseInvoiceForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseCredit),
    component: PurchaseCreditForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseCreditQuick),
    component: QuickPurchaseCreditForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseAllocation),
    component: PurchaseAllocationForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseVatOnly),
    component: PurchaseVatOnlyForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchasePayment),
    component: PurchasePaymentForm,
    parent: 'PURCHASING',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseRefund),
    component: PurchaseRefundForm,
  },
  {
    ...modalFieldsFromEntity(entities.SuggestedPayment),
    component: SuggestedPaymentForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.SuggestedPayment, 'Temp/:uuid'),
    render: (props: any) => <SuggestedPaymentForm {...props} temporaryDefault={true} />,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierAccountStatus),
    component: SupplierAccountStatusForm,
  },
  {
    ...modalFieldsFromEntity(entities.VendorRating),
    component: VendorRatingForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchasePaymentTerm),
    component: PurchasePaymentTermForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchasePaymentMethod),
    component: PurchasePaymentMethodForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOpeningBalance),
    component: PurchaseOpeningBalanceForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseLedgerJournal),
    component: PurchaseLedgerJournalForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesNoteType),
    component: SalesNoteTypeForm,
  },
  {
    url: '/Sales/TillExperiment/Details/:id',
    component: TillExperiment,
    screenReference: 'SALES_NOTE_TYPES',
  },
  {
    ...modalFieldsFromEntity(entities.InvoiceGenerationCriteria, ''),
    component: InvoiceGenerationCriteriaResult,
    exact: true,
    parent: 'SALES',
  },
  {
    ...modalFieldsFromEntity(entities.SalesAttributeConfig),
    component: SalesAttributeConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.DocumentConfig),
    url: `${entities.DocumentConfig.formUrlSales}:id`,
    component: SalesDocumentConfigForm,
    screenReference: `${entities.DocumentConfig.screenReferenceSales}`,
  },
  {
    ...modalFieldsFromEntity(entities.SalesOrder),
    component: SalesOrderForm,
    exact: true,
  },
  {
    url: '/Sales/Order/Template/:id',
    component: SalesOrderForm,
    exact: true,
    screenReference: 'SALES_ORDERS_CREATE',
  },
  {
    url: '/Sales/Order/Template/:id/:customerid',
    component: SalesOrderForm,
    exact: true,
    screenReference: 'SALES_ORDERS_CREATE',
  },
  {
    ...modalFieldsFromEntity(entities.SalesOrder, ':id/:customerid'),
    component: SalesOrderForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.Quotation),
    component: QuotationForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.Quotation, ':id/:customerid'),
    component: QuotationForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.Carrier),
    component: CarrierForm,
  },
  {
    ...modalFieldsFromEntity(entities.Haulier),
    component: HaulierForm,
  },
  {
    ...modalFieldsFromEntity(entities.PacTerminal),
    component: PacTerminalForm,
  },
  {
    ...modalFieldsFromEntity(entities.DeliveryMethod),
    component: DeliveryMethodsForm,
  },
  {
    ...modalFieldsFromEntity(entities.DeliveryRoute),
    component: DeliveryRouteForm,
  },
  {
    ...modalFieldsFromEntity(entities.PaymentMethod),
    component: PaymentMethodForm,
  },
  {
    ...modalFieldsFromEntity(entities.EbayConfig),
    component: EbayConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.AmazonConfig),
    component: AmazonConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.ShopifyConfiguration),
    component: ShopifyConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.PixSellConfiguration),
    component: PixSellConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.WooCommerceConfiguration),
    component: WooCommerceConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.EtsyConfiguration),
    component: EtsyConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.BigCommerceConfiguration),
    component: BigCommerceConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.MagentoConfiguration),
    component: MagentoConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.WebConfiguration),
    component: WebConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.EdiConfiguration),
    component: EdiConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.ChannelConfig),
    component: ChannelConfigForm,
  },
  {
    ...modalFieldsFromEntity(entities.WebPage),
    component: WebPageForm,
  },
  {
    ...modalFieldsFromEntity(entities.WebMediaFolder),
    component: WebMediaFolderForm,
    parent: 'WEB',
  },
  {
    ...modalFieldsFromEntity(entities.WebPromotionalCode),
    component: WebPromotionalCodeForm,
  },
  {
    ...modalFieldsFromEntity(entities.TradeCounterTerminal),
    component: TradeCounterTerminalForm,
  },
  {
    ...modalFieldsFromEntity(entities.TradeCounterReconcile),
    component: TradeCounterReconcileForm,
  },
  {
    ...modalFieldsFromEntity(entities.EventType),
    component: TypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryResult),
    component: ResultForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryAttributeConfig),
    component: AttributeForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryEvent),
    exact: true,
    component: EventForm,
  },
  {
    url: '/Diary/RecurringEvent/Details/:id',
    exact: true,
    component: EventForm,
    screenReference: 'DIARY',
  },
  {
    ...modalFieldsFromEntity(entities.DiaryEvent, ':id/Customer/:customerId'),
    exact: true,
    component: EventForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryEvent, ':id/Supplier/:supplierId'),
    exact: true,
    component: EventForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryEvent, ':id/SalesOrder/:salesOrderId'),
    exact: true,
    component: EventForm,
  },
  {
    ...modalFieldsFromEntity(entities.DiaryEvent, ':id/PurchaseOrder/:purchaseOrderId'),
    exact: true,
    component: EventForm,
  },
  {
    ...modalFieldsFromEntity(entities.EventGlobalAmend),
    component: DiaryGlobalAmendForm,
    parent: 'DIARY',
  },
  {
    ...modalFieldsFromEntity(entities.Dimension),
    component: DimensionForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalBusinessCategory),
    component: NominalBusinessCategoryForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalProductCategory),
    component: NominalProductCategoryForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalReportCategory),
    component: ReportCategoryForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalCommonAccountSet),
    component: CommonAccountSetForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalCustomerAccountSet),
    component: CustomerAccountSetForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalSupplierAccountSet),
    component: SupplierAccountSetForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalProductAccountSet),
    component: ProductAccountSetForm,
  },
  {
    ...modalFieldsFromEntity(NominalWarehouseAccountSet),
    component: NominalWarehouseAccountSetForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalNoteType),
    component: NominalNoteTypeForm,
  },
  {
    ...modalFieldsFromEntity(entities.Nominal),
    component: NominalForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalBudgetProfile),
    component: NominalBudgetProfileForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalJournal),
    component: JournalForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalTemplate),
    component: TemplateForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalAccrualPrepayment),
    component: AccrualPrepaymentForm,
  },
  {
    ...modalFieldsFromEntity(entities.NominalReport),
    component: ReportForm,
  },
  {
    ...modalFieldsFromEntity(entities.VatBusinessCategory),
    component: VatBusinessCategoryForm,
  },
  {
    ...modalFieldsFromEntity(entities.VatProductCategory),
    component: VatProductCategoryForm,
  },
  {
    ...modalFieldsFromEntity(entities.VatAuthority),
    component: AuthorityForm,
  },
  {
    ...modalFieldsFromEntity(entities.VatRate),
    component: RateForm,
  },
  {
    ...modalFieldsFromEntity(entities.VatReturn),
    component: ReturnForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesPaymentTerm),
    component: SalesPaymentTermForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesCreditReason),
    component: CreditReasonForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesInvoice),
    component: InvoiceForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesCredit),
    component: CreditForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesReceipt),
    component: ReceiptForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.SalesRefund),
    component: RefundForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesAllocation),
    component: AllocationForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesOpeningBalance),
    component: SalesOpeningBalanceForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesBadDebt),
    component: BadDebtForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesLedgerJournal),
    component: SalesLedgerJournalForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesTransaction),
    component: TransactionForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesVatOnly),
    component: VatOnlyForm,
  },
  {
    ...modalFieldsFromEntity(entities.Bank),
    component: BankForm,
  },
  {
    ...modalFieldsFromEntity(entities.BankPaymentReceipt),
    component: BankPaymentReceiptForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.BankReconciliation),
    component: BankReconciliationForm,
  },
  {
    ...modalFieldsFromEntity(entities.Bank),
    url: '/Bank/BankFeed/Details/:id',
    component: BankFeedForm,
  },
  {
    ...modalFieldsFromEntity(entities.BankTransfer),
    component: BankTransferForm,
  },
  {
    ...modalFieldsFromEntity(entities.BankRevaluation),
    component: BankRevaluationForm,
  },
  {
    url: '/System/SmtpConfig/Details/:id',
    component: SmtpConfigForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SmtpLog/Details/:id',
    component: SmtpLogForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/Company/Details/:id',
    component: SystemCompanyForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SystemUser/Details/:id',
    component: SystemUserForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SystemPrinter/Details/:id',
    component: SystemPrinterForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/RegisteredDevice/Details/:id',
    component: RegisteredDeviceForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SystemConfig',
    component: SystemConfigForm,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SystemLicense',
    component: SystemLicenseForm,
    screenReference: 'RESTRICTED',
  },
  {
    url: '/System/JobReport/Details/:id',
    component: JobReportForm,
    screenReference: 'SYSTEM',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrder),
    component: PurchaseOrderForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrder, ':id/:supplierid'),
    component: PurchaseOrderForm,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.SupplierReturn),
    component: SupplierReturnForm,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseRequirementDetail),
    component: PurchaseRequirementDetailForm,
    parent: 'SHORTAGES',
  },
  {
    ...modalFieldsFromEntity(entities.AssemblyRequirementDetail),
    component: AssemblyRequirementDetailForm,
  },
  {
    ...modalFieldsFromEntity(entities.BusinessRule),
    component: BusinessRuleForm,
  },
  {
    ...modalFieldsFromEntity(entities.OptionalMandatoryFields),
    component: OptionalMandatoryFieldsForm,
  },
  {
    ...modalFieldsFromEntity(entities.SalesOrderLineAuditByField, ':id/:lineid'),
    component: SalesOrderLineAuditForm,
    parent: 'SALES',
  },
  {
    ...workspaceFieldsFromEntity(entities.Acknowledgement, ':batch'),
    component: AcknowledgementWorkspace,
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.ManualDispatch, ''),
    component: ManualDispatchForm,
    parent: 'SALES',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseAttributeConfig),
    component: PurchaseAttributeConfigForm,
  },
  {
    url: `${entities.DocumentConfig.formUrlPurchasing}:id`,
    component: PurchaseDocumentConfigForm,
    screenReference: entities.DocumentConfig.screenReferencePurchasing,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrderAssignment),
    component: PurchaseAssignmentForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.MinMaxCalculationResultDetails, ':jobid'),
    component: MinMaxCalculationResultDetailsWorkspace,
    parent: 'PRODUCTS',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.CustomerPriceReviewLine, ':jobid/:customer'),
    component: CustomerPriceReviewLineWorkspace,
    parent: 'CUSTOMER',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.SupplierPriceReviewLine, ':jobid/:supplier'),
    component: SupplierPriceReviewLineWorkspace,
    parent: 'SUPPLIER',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.AutomatedCreditControlScheduledEmail),
    component: AutomatedCreditControlResultWorkspace,
    parent: 'CUSTOMER',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.ProductGlobalAmendResult, ':jobid'),
    render: createJobFilterWorkspace(ProductGlobalAmendResultsWorkspace),
    parent: 'PRODUCTS',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.CustomerGlobalAmendResult, ':jobid'),
    render: createJobFilterWorkspace(CustomerGlobalAmendResultsWorkspace),
    parent: 'CUSTOMERS',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.SupplierGlobalAmendResult, ':jobid'),
    render: createJobFilterWorkspace(SupplierGlobalAmendResultsWorkspace),
    parent: 'SUPPLIERS',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.EventGlobalAmendResult, ':jobid'),
    render: createJobFilterWorkspace(DiaryGlobalAmendResultsWorkspace),
    parent: 'DIARY',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.ProductPriceMatrixPreview, ':jobid/:uuid'),
    render: createJobFilterWorkspace(ProductPriceMatrixPreviewWorkspace),
    parent: 'PRODUCTS',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.CustomerPriceMatrixPreview, ':jobid/:uuid'),
    render: createJobFilterWorkspace(CustomerPriceMatrixPreviewWorkspace),
    parent: 'CUSTOMERPRICING',
    operations: 0,
  },
  {
    ...workspaceFieldsFromEntity(entities.SupplierPriceMatrixPreview, ':jobid/:uuid'),
    render: createJobFilterWorkspace(SupplierPriceMatrixPreviewWorkspace),
    parent: 'SUPPLIERPRICING',
    operations: 0,
  },
  {
    ...modalFieldsFromEntity(entities.SalesReceipt, ':id/:bank/:amount/:date/:reference'),
    component: ReceiptForm,
  },
  {
    ...modalFieldsFromEntity(entities.BankPaymentReceipt, ':id/:type/:bank/:amount/:date/:reference'),
    component: BankPaymentReceiptForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.Allocation, ':jobid'),
    render: renderBulkAllocationResultsWorkspace,
    parent: 'PRODUCTS',
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrderShortageGenerationCriteria, ''),
    component: PurchaseRequirementSingleGenerationForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.PurchaseRequirementDetail, ':id'),
    component: PurchaseRequirementDetailWorkspace,
  },
  {
    ...workspaceFieldsFromEntity(entities.PurchaseRequirementDetailPreview, ':salesorderid/:jobid'),
    render: renderPurchaseRequirementDetailPreviewWorkspace,
    parent: 'PURCHASING',
  },
  {
    ...modalFieldsFromEntity(entities.ImportedAmazonOrder, ':id'),
    component: ImportedAmazonOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedEbayOrder, ':id'),
    component: ImportedEbayOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedShopifyOrder, ':id'),
    component: ImportedShopifyOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedPixSellOrder, ':id'),
    component: ImportedPixSellOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedWooCommerceOrder, ':id'),
    component: ImportedWooCommerceOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedEtsyOrder, ':id'),
    component: ImportedEtsyOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedBigCommerceOrder, ':id'),
    component: ImportedBigCommerceOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedMagentoOrder, ':id'),
    component: ImportedMagentoOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedEdiOrder, ':id'),
    component: ImportedXediOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.ImportedWebOrder, ':id'),
    component: ImportedWebOrderForm,
  },
  {
    ...modalFieldsFromEntity(entities.JobTitle),
    component: JobTitleForm,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerContractReference),
    component: CustomerContractReferenceForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.DocumentSummary, ':jobid'),
    render: createJobFilterWorkspace(DocumentSummary),
    exact: true,
  },
  {
    ...modalFieldsFromEntity(entities.PurchaseOrderLineAuditByField, ':id/:lineid'),
    component: PurchaseOrderLineAuditForm,
    parent: 'PURCHASING',
  },
  {
    ...workspaceFieldsFromEntity(entities.CustomerStatement, ':jobid'),
    render: createJobFilterWorkspace(CustomerStatementResultWorkspace),
    exact: true,
  },
  {
    ...workspaceFieldsFromEntity(entities.PurchaseOrder, ':jobid'),
    render: createJobFilterWorkspace(PurchaseOrderWorkspace),
    parent: 'PURCHASING',
    exact: true,
  },
  {
    ...workspaceFieldsFromEntity(entities.NominalTransaction, ':nominalAccountId'),
    shortTitle: 'Transactions',
    icon: 'nominaltransactions',
    render: createPredefinedFilterWorkspace(NominalTransactionWorkspace),
    exact: true,
  },
  {
    ...modalFieldsFromEntity({
      ...StockTake,
      formUrl: StockTake.oldFormUrl,
      screenReference: `${StockTake.screenReference as string}_${StockTakeType.Progressive}_Old`,
    }),
    component: ProductsStockTakeProgressiveForm,
  },
  {
    ...modalFieldsFromEntity({
      ...StockTake,
      screenReference: `${StockTake.screenReference as string}_${StockTakeType.Progressive}`,
    }),
    component: P4StockTakeProgressiveForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.StockTakeLine, ':stockTakeId'),
    component: StockTakeLineWorkspace,
  },
  {
    ...modalFieldsFromEntity(entities.StockTakeStatus),
    component: StockTakeStatusForm,
  },
  {
    ...modalFieldsFromEntity(entities.Assembly, ':id/:productid'),
    component: AssemblyForm,
  },
  {
    ...modalFieldsFromEntity(entities.Assembly),
    component: AssemblyForm,
  },
  {
    ...modalFieldsFromEntity(entities.AssemblyAssignment),
    component: AssemblyAssignmentForm,
  },
  {
    url: '/Products/Assembly/Reservation/:id',
    component: AssemblyReservationTreeSpaceWithContext,
    screenReference: 'RESERVE_COMPONENTS',
  },
  {
    ...workspaceFieldsFromEntity(entities.AutomatedCreditControlResult, ':jobid'),
    component: AutomatedCreditControlResultWorkspace,
  },
  {
    ...modalFieldsFromEntity(entities.CustomerGlobalContact),
    component: CustomerGlobalContactForm,
  },
  {
    ...workspaceFieldsFromEntity(entities.BinReplenishmentDetail, ':jobid'),
    component: BinReplenishmentDetailWorkspace,
  },
  {
    ...modalFieldsFromEntity(entities.ConsolidatedPickGroup, ':id'),
    component: ConsolidatedPickGroupForm,
  },
  {
    ...modalFieldsFromEntity(entities.ConsolidatedPick, ':id'),
    component: ConsolidatedPickForm,
  },
];

export const systemRoutes: Array<IMenuEntry> = [
  {
    url: '/System/SmtpConfig/Workspace',
    component: SmtpConfigWorkspace,
    screenReference: 'SYSTEM',
  },
  { url: '/System/SmtpLog/Workspace', component: SmtpLogWorkspace, screenReference: 'SYSTEM' },
  { url: '/System/Company/Workspace', component: CompanyWorkspace, screenReference: 'SYSTEM' },
  {
    url: '/System/SystemUser/Workspace',
    component: SystemUserWorkspace,
    screenReference: 'SYSTEM',
  },
  {
    url: '/System/SystemPrinter/Workspace',
    component: SystemPrinterWorkspace,
    screenReference: 'SYSTEM',
  },
  { url: entities.LoggedInUser.workspaceUrl, component: LoggedInUserWorkspace, screenReference: 'SYSTEM' },
  { url: entities.JobInfo.workspaceUrl, component: JobsWorkspace, screenReference: 'SYSTEM' },
  {
    url: '/System/RegisteredDevice/Workspace',
    component: RegisteredDevicesWorkspace,
    screenReference: 'SYSTEM',
  },
];

const useMenuOptions = (currentFolder?: Maybe<number>) => {
  //This is pretty consistently just 'Array<IMenuEntry>' in practise, however there are moments it gets
  //at least briefly set to IAppMenu, so setting the type like this for clarity/exception handling.
  const menuRef = useRef<IAppMenu | Array<IMenuEntry>>([]);

  const dispatch = useDispatch();
  const [screenUpdate] = useMutation<IScreenFolderMutationResult, IScreenFolderMutationVariables>(SystemScreen.update!);
  const [folderListUpdate] = useMutation<ISystemFolderAndScreenMutationResult, ISystemFolderAndScreenMutationVariables>(
    SystemFolder.multiUpdate
  );

  const resetMenuOptions = (menu: IAppMenu = {} as IAppMenu): IAppMenu => {
    const listOptions: IAppMenu = menu || allOptions;
    menuRef.current = listOptions;
    return listOptions;
  };

  const searchOptions = (term: string): Array<IMenuEntry> => {
    const menu = Object.assign({}, menuRef.current);
    menuRef.current = [];

    const uniqueKeys = new Set();

    const tempMenus: Array<IMenuEntry> = filterBySearchObject(menu as IAppMenu, term);
    const toBeAdded: Array<IMenuEntry> = [];

    for (const tempMenu of tempMenus) {
      const key = `${tempMenu.url}-${tempMenu.title}`;

      let addMenuItem = true;
      for (const menuR of menuRef.current) {
        if (tempMenu.title === menuR.title) {
          addMenuItem = false;
          break;
        }
      }
      if (addMenuItem && !tempMenu.excludeSearch && !uniqueKeys.has(key)) {
        uniqueKeys.add(key);
        toBeAdded.push(tempMenu);
      }
    }
    menuRef.current.push(...toBeAdded);

    return menuRef.current;
  };

  const escapeRegExp = (string: string) => {
    return string?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const filterBySearchObject = (
    menu: IAppMenu | ISubMenuSection,
    term: string,
    parentHeader: string | undefined = undefined
  ): Array<IMenuEntry> => {
    const menuHeaders = Object.keys(menu) as Array<keyof IAppMenu>;
    return menuHeaders.flatMap((menuKey) => {
      const entry = menu[menuKey];
      const rootHeader: string = parentHeader || menuKey;

      return filterBySearchArray(entry, term, rootHeader);
    });
  };

  const filterBySearchArray = (
    menu: Array<IMenuEntry | ISubMenuSection>,
    term: string,
    parentHeader: string
  ): Array<IMenuEntry> => {
    const termWords: Array<string> = term.trim().split(' ');
    const regexTerm: RegExp = new RegExp(
      `^${termWords.map((_term) => `(?=.*${escapeRegExp(_term)})`).join('')}.*$`,
      'i'
    );

    return menu.flatMap((entry) => {
      if (isMenuEntry(entry)) {
        if (!entry.ignoreInSearch && entry.title) {
          const match = entry.title.match(regexTerm);
          if (match) {
            return [scoreMatch(entry, parentHeader, term)];
          }
        }
      } else {
        return filterBySearchObject(entry, term, parentHeader);
      }
      return [];
    });
  };

  const scoreMatch = (matched: IMenuEntry, header: string, term: string): IMenuEntry => {
    const termBits = term.split(' ');
    let count = 0;
    for (let i = 0; i < termBits.length; i++) {
      const regEx: RegExp = new RegExp(termBits[i], 'i');
      const result: RegExpMatchArray | null = header.match(regEx);
      if (result) {
        count++;
      }
      const menuResult = matched.title?.match(regEx);
      if (menuResult) {
        count += 2;
      }
    }

    return Object.assign({}, matched, { count: count, section: header });
  };

  const addRemoveCustomScreen = (variables: IScreenFolderMutationVariables): void => {
    screenUpdate({
      variables: variables,
    }).then((data) => {
      if (data.data?.screenUpdate?.result) {
        dispatch(menuActions.serviceDataToMenu(data.data.screenUpdate.result));
      }
    });
  };

  const addRemoveCustomFolder = (variables: ISystemFolderAndScreenMutationVariables): void => {
    folderListUpdate({
      variables: variables,
    }).then((data) => {
      if (data.data?.folderListUpdate?.screens) {
        for (let i = data.data.folderListUpdate.screens.length - 1; i >= 0; i--) {
          if (data.data.folderListUpdate.screens[i]?.deleted) {
            data.data.folderListUpdate.screens.splice(i, 1);
          }
        }
        dispatch(menuActions.serviceBulkToMenu(data.data.folderListUpdate));
      }
    });
  };

  const getFavoritesMenu = (favs: ISystemMenuList, menu?: IAppMenu): Array<IMenuEntry> => {
    return favoritesMenu(favs, undefined, menu);
  };

  const favoritesMenu = (
    favs: ISystemMenuList,
    menuLevel: ISubMenuSection | undefined = undefined,
    menu: IAppMenu | undefined = undefined
  ): Array<IMenuEntry> => {
    const newMenu: Array<IMenuEntry> = [];
    if (!favs.menu) {
      return newMenu;
    }

    let fullMenu: IAppMenu | ISubMenuSection | undefined = menu;
    if (menuLevel) {
      fullMenu = menuLevel;
    }

    if (fullMenu) {
      const menuList = Object.keys(favs.menu);
      const menuHeaders = Object.keys(fullMenu);
      for (const menuHeader of menuHeaders) {
        for (const menuHeaderMenu of fullMenu[menuHeader as keyof IAppMenu]) {
          if (isMenuEntry(menuHeaderMenu)) {
            if (menuHeaderMenu.duplicate) {
              continue;
            }
            for (const menuListItem of menuList) {
              const currentFavourite = favs.menu[menuListItem];

              const screenReference = Array.isArray(menuHeaderMenu.screenReference)
                ? `[${menuHeaderMenu.screenReference.join(', ')}]`
                : menuHeaderMenu.screenReference;

              if (
                screenReference === menuListItem &&
                !menuHeaderMenu.report &&
                ((currentFolder === null && !currentFavourite.folder) ||
                  (currentFavourite.folder && currentFavourite.folder?.id === currentFolder))
              ) {
                const { sortOrder, id, uuid, concurrencyVersion, folder } = currentFavourite;
                newMenu.push(
                  Object.assign({}, menuHeaderMenu, {
                    sortOrder: sortOrder,
                    id: id,
                    uuid: uuid,
                    concurrencyVersion: concurrencyVersion,
                    folder: folder,
                  })
                );
              }
            }
          } else {
            const ops: Array<IMenuEntry> = favoritesMenu(favs, menuHeaderMenu);
            if (ops && ops.length > 0) {
              ops.forEach((menuItem) => {
                if (
                  !newMenu.find((existingMenuItem) => existingMenuItem.screenReference === menuItem.screenReference)
                ) {
                  newMenu.push(menuItem);
                }
              });
            }
          }
        }
      }
    }

    return newMenu;
  };

  const moveItemToFolder = (
    dispatch: any,
    screenReference: string,
    folderId: Maybe<number>,
    customMenu: ISystemMenuList
  ): void => {
    let updatedCustomMenu: ISystemMenuList = customMenu;

    const sortOrderPointer = getIn(updatedCustomMenu, ['menu', screenReference, 'sortOrder']);
    let folder = getIn(updatedCustomMenu, ['menu', screenReference, 'folder']);

    let orderCounter = folderId ? 0 : Object.keys(getIn(updatedCustomMenu, ['folders'])).length;

    updatedCustomMenu = setIn(updatedCustomMenu, ['menu', screenReference, 'folder'], { id: folderId });
    const newState: ISystemMenuList = Object.assign({}, updatedCustomMenu);
    const menuHeaders = Object.keys(newState.menu);
    let sortValue;

    if (!!folder) {
      folder = folder.id;
    }

    for (const menuHeader of menuHeaders) {
      const newStateMenuHeader: any = newState.menu[menuHeader];
      const itemFolder = newStateMenuHeader.folder ? newStateMenuHeader.folder.id : newStateMenuHeader.folder;
      if (newStateMenuHeader.sortOrder > sortOrderPointer && folder === itemFolder) {
        sortValue = getIn(updatedCustomMenu, ['menu', menuHeader, 'sortOrder']);
        updatedCustomMenu = setIn(updatedCustomMenu, ['menu', menuHeader, 'sortOrder'], sortValue - 1);
      }

      const menuFolderId = !!newStateMenuHeader.folder ? newStateMenuHeader.folder.id : null;
      if (menuFolderId === folderId) {
        orderCounter++;
      }
    }

    const foldersHeader = Object.keys(newState.folders) as Array<`${number}`>;
    for (const folderHeader of foldersHeader) {
      if (newState.folders[folderHeader].sortOrder > sortOrderPointer && folder === null) {
        sortValue = getIn(updatedCustomMenu, ['folders', folderHeader, 'sortOrder']);
        updatedCustomMenu = setIn(updatedCustomMenu, ['folders', folderHeader, 'sortOrder'], sortValue - 1);
      }
    }

    updatedCustomMenu = setIn(updatedCustomMenu, ['menu', screenReference, 'sortOrder'], orderCounter);
    const menuArray = convertToArray(updatedCustomMenu);

    folderListUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          folders: normalizeVariables(menuArray[1]),
          screens: normalizeVariables(menuArray[0]),
        },
      },
    }).then((data) => {
      dispatch(menuActions.serviceBulkToMenu(data.data?.folderListUpdate));
    });
  };

  const convertToArray = (menu: ISystemMenuList): Array<any> => {
    const newState: ISystemMenuList = Object.assign({}, menu);
    const menuHeaders: Array<string> = Object.keys(newState.menu);
    const foldersHeaders = Object.keys(newState.folders) as Array<`${number}`>;
    const menuArray: Array<any> = [];
    const foldersArray: Array<any> = [];

    for (const folderHeader of foldersHeaders) {
      foldersArray.push(newState.folders[folderHeader]);
    }
    for (const menuHeader of menuHeaders) {
      menuArray.push(newState.menu[menuHeader]);
    }

    return [menuArray, foldersArray];
  };

  return {
    resetMenuOptions,
    searchOptions,
    getFavoritesMenu,
    moveItemToFolder,
    addRemoveCustomScreen,
    addRemoveCustomFolder,
  };
};

export default useMenuOptions;
